/**
 * sourceBreakdown (#2442) — the per-line source drill-down for the Deckungsbeitrag
 * detail panel. Given a ``DeckungsbeitragRow``, a cascade-line bucket key and the
 * crop_cycle's served ``OperationInfo[]``, it returns the individual inputs that
 * sum to that line plus a reconciling total.
 *
 * The reconciliation guarantee is the whole point, so this ports the worker
 * materialiser's arithmetic EXACTLY (``worker/src/services/materialisers/
 * deckungsbeitrag.py``): the operation → bucket classification
 * (``_OPERATION_TYPE_BUCKET`` / ``_COST_BUCKET_BY_LEDGER``) and the per-op labour /
 * machinery € derivation (``_labour_cost`` / ``_machinery_cost``). Costs are summed
 * RAW (the engine sums raw then rounds once), the total is ``round2``-ed, and the
 * line reconciles when the total matches the persisted bucket to the cent — else the
 * UI shows an honest "Σ stimmt nicht exakt" note instead of a fake ✓.
 *
 * Three breakdown kinds:
 *   - ``operations``  — Leistung / Dünger/PSM / Löhne / Maschinen / Sonstige (and a
 *                       booked Saatgut cost): Σ over the attributed operations.
 *                       HOF-299 — Dünger/PSM is ONE cascade line over the fact's TWO
 *                       buckets; see ``DUENGER_PSM_LINE`` / ``lineBuckets``.
 *   - ``saatgut_estimate`` — the #2441 Fläche × Dichte = Pflanzen × Preis valuation.
 *   - ``allocations`` — Gemeinkosten: Σ over the persisted overhead-allocation audit array.
 *
 * #2508 — the output is TWO generic tables, never a crop- or line-specific template:
 *   - ``factors[]`` + ``result`` — the ``Rechenweg`` (how the line was DERIVED). Emitted
 *     only by a derived line; a plain Σ-of-bookings line emits none, and the render layer
 *     shows the table iff ``factors.length > 0``.
 *   - ``items[]`` + ``total`` — the ``Einzelposten`` (``Datum | Posten | Betrag``), every
 *     deep-link living INSIDE Posten so no column sits behind the number.
 *     A material input (Dünger/PSM) is one row per article — ``<Kind> · <Artikel> · Menge ×
 *     Ø`` — from the fact's valued Posten or, on a fact without one, the operation's own
 *     booking (HOF-467 follow-up; ``materialLines``).
 * Each link carries its own ``label`` naming its DESTINATION (the Kultur, the price, the
 * Buchung) rather than the destination's category, so the component prints whatever the
 * data says and holds no crop/line branch of its own.
 *
 * Pure + I/O-free (twice-testable): no React, no navigation. It DOES pre-format the
 * factor/link display text (via the app's own pure de-DE ``format`` helpers), because the
 * whole point of the #2508 contract is that the render layer interprets nothing.
 */
import type { LabourEntry, MachineryEntry, OperationInfo } from '../../../hooks/useDeckungsbeitrag';
import { inputPostenNote } from './inputMark';
import { postenNote } from './leistungMark';
// #2538 — the Preisspiegel catalog is the single authority on which Kategorie an item lives
// under, so the deep link's ?kat= is DERIVED from the item rather than guessed from the crop.
// Pure data + pure helpers (no React, no data-plane), so the app-boundary convention holds.
import { findItem, findKategorie } from '../preisspiegel/catalog';
import { formatBookPrice, formatBookQty, formatEur, formatNumber, formatPostenQty, postenUnitLabel } from './format';
import type { OperationInput } from '../../../hooks/useDeckungsbeitrag';
import type {
  DeckungsbeitragRow,
  EstablishmentDetail,
  InputPostenBlock,
  InputPostenItem,
  InvestitionClass,
  InvestitionComponents,
  InvestitionKey,
  LabourPostenItem,
  LabourPostenKind,
  LeistungEstimate,
  LeistungPostenItem,
  SaatgutStand,
  StandProvenance,
  StandState,
  StandZeroState,
  ZeroStateStand,
} from './types';
import { STAND_ZERO_STATES } from './types';

// ── engine-parity bucketing tables (ported verbatim from the materialiser) ──────
/** operation ``type`` → material bucket (harvest/pick carry no material bucket). */
const OPERATION_TYPE_BUCKET: Record<string, string> = {
  sow: 'saatgut',
  fertilize: 'duenger',
  spray: 'psm',
  // #2262 ontology synonyms (adapter emits these too) — same buckets.
  sowing: 'saatgut',
  fertilisation: 'duenger',
  plant_protection: 'psm',
  // HOF-519 — a field work's money is machinery. Its absence here was the whole of the
  // „Arbeitsgang unter Sonstige Direktkosten" defect: ``materialCostBucket`` found no type
  // bucket, fell to its non-pick ``sonstige`` default, and ten Maschinen bookings were
  // counted and listed one line below where they belong. Mirrors the materialiser's
  // ``_OPERATION_TYPE_BUCKET``.
  field_work: 'maschinen',
};

/** ledger_category L2 id → non-``sonstige`` direct-cost bucket (``_COST_BUCKET_BY_LEDGER``). */
const COST_BUCKET_BY_LEDGER: Record<string, string> = {
  exp_seed: 'saatgut',
  exp_fertilizer: 'duenger',
  exp_cropprotection: 'psm',
  exp_wages: 'loehne',
  exp_socialsec: 'loehne',
  exp_contracting: 'maschinen',
  exp_fuel: 'maschinen',
  exp_maintenance: 'maschinen',
  // HOF-518 — Pacht is its own bucket now, not a Sonstige row. Mirrors the engine's
  // ``_COST_BUCKET_BY_LEDGER``; left out, a booked Pacht row is counted under Sonstige here
  // while the worker's € sits in ``land``, and BOTH lines mint „Σ stimmt nicht".
  exp_lease: 'land',
};

const DIRECT_COST_BUCKETS = new Set(['saatgut', 'duenger', 'psm', 'loehne', 'maschinen', 'sonstige', 'land']);

/**
 * HOF-299 — the merged „Dünger/PSM" cascade line. The fact keeps ``direct_costs.duenger`` and
 * ``direct_costs.psm`` as two buckets (the materialiser's ``_DIRECT_COST_BUCKETS`` is unchanged,
 * and the MyFarm24 kosten-report reconciliation needs the two apart); the SCREEN shows one
 * line whose €, Σ-of-bookings, record count and zero reason span both. The single-bucket keys
 * stay valid line keys for the pure layer, so nothing that reads a bucket by name breaks —
 * only the cascade stops rendering them.
 */
export const DUENGER_PSM_LINE = 'duenger_psm';
const DUENGER_PSM_MEMBERS: readonly string[] = ['duenger', 'psm'];

/**
 * 2026-10-07 — the labour lines the drawer no longer prints on their own. Pflück-Nebenkosten
 * folds into Pflücklohn (same layer, same subtotal: both sit in ``leistung_nach_pfluecken``
 * and in Arbeitserledigung), and Pflanzarbeit into Löhne beside Pflegearbeit and the
 * unassigned work — one wage line for every hour that is not a harvest hour. The fact keeps
 * its buckets; only the reading changes, exactly as HOF-299 did for Dünger/PSM.
 */
const PFLUECK_MEMBERS: readonly string[] = ['pflueck', 'nebenkosten'];
const LOEHNE_MEMBERS: readonly string[] = ['loehne', 'pflanzung'];

/** The direct-cost bucket(s) a cascade-line key spans — a merged line spans two, every other key itself. */
export function lineBuckets(key: string): readonly string[] {
  if (key === DUENGER_PSM_LINE) return DUENGER_PSM_MEMBERS;
  if (key === 'pflueck') return PFLUECK_MEMBERS;
  if (key === 'loehne') return LOEHNE_MEMBERS;
  return [key];
}

/**
 * HOF-299 — the canonical cascade-line key for a (possibly bookmarked) ``?line=`` value. A
 * pre-merge ``duenger`` / ``psm`` deep link opens the merged line instead of nothing; every
 * other key passes through untouched.
 */
export function canonicalLineKey(key: string | null): string | null {
  if (key === null) return null;
  if (DUENGER_PSM_MEMBERS.includes(key)) return DUENGER_PSM_LINE;
  // 2026-10-07 — a bookmarked Nebenkosten / Pflanzarbeit line (real or pending) opens the line
  // it now lives in.
  if (key === 'nebenkosten' || key === 'pending:nebenkosten') return 'pflueck';
  if (key === 'pflanzung' || key === 'pending:pflanzung') return 'loehne';
  return key;
}

/** The cascade-line keys that carry a source breakdown (subtotals/results do not). */
// HOF-518 — ``land`` is here for the BOOKED Pacht only (`exp_lease` bookings Σ to the line
// like any other cost). The IMPUTED rate has no bookings by construction and is intercepted
// before this set is consulted, by the ``land_estimate`` branches in ``lineSelectability``
// and the breakdown dispatch.
const OPERATIONS_BUCKETS = new Set(['leistung', 'saatgut', 'duenger', 'psm', DUENGER_PSM_LINE, 'loehne', 'maschinen', 'sonstige', 'land']);
// HOF-518 — omitting ``land`` here is what left the real Landkosten row disabled while its
// pending twin opened fine (codex r2); it now arrives through ``OPERATIONS_BUCKETS`` above.
/**
 * #4172 wave 2 — the five labour buckets. They are NOT in ``OPERATIONS_BUCKETS``: their
 * evidence is the fact's own ``labour_posten``, not the cycle's served operations, and
 * handing one to ``buildOperationsBreakdown`` would Σ operations that carry no wage to 0
 * against a four-figure line and mint a false „Σ stimmt nicht" — the #2695 E argument, one
 * bucket family over.
 */
const LABOUR_BUCKET_KEYS = new Set(['pflueck', 'nebenkosten', 'verpackung', 'kuehlung', 'pflanzung']);

const SOURCE_BUCKET_KEYS = new Set([...OPERATIONS_BUCKETS, 'gemeinkosten', ...LABOUR_BUCKET_KEYS]);

/** The cascade lines whose Posten may drop a material descriptor the op TYPE already names (#2460). */
const MATERIAL_LINES = new Set(['duenger', 'psm', DUENGER_PSM_LINE]);

/** German label for an operation ``type`` — the breakdown row's primary descriptor. */
const OP_TYPE_LABEL: Record<string, string> = {
  pick: 'Pflückung',
  harvest: 'Ernte',
  sow: 'Aussaat',
  sowing: 'Aussaat',
  fertilize: 'Düngung',
  fertilisation: 'Düngung',
  spray: 'Pflanzenschutz',
  plant_protection: 'Pflanzenschutz',
  // HOF-307 — the journal's Works/Machines rows (money on the machinery leg → Maschinen).
  field_work: 'Arbeitsgang',
  // #2511 — the whole-cycle Löhne/Maschinen cost aggregate (MyFarm24 running-costs lumps).
  // These used to omit ``type`` and fall through to the generic "Buchung"; they now carry an
  // explicit marker, which needs its own German label so the raw ontology value never leaks.
  cost_booking: 'Kostenbuchung',
};

/** German material label per ledger id — the material-cost row's secondary descriptor. */
const LEDGER_MATERIAL_LABEL: Record<string, string> = {
  exp_seed: 'Saatgut',
  exp_fertilizer: 'Dünger',
  exp_cropprotection: 'Pflanzenschutz',
  exp_fuel: 'Treibstoff',
  exp_contracting: 'Lohnunternehmer',
  exp_maintenance: 'Instandhaltung',
};

export const RECONCILE_TOLERANCE = 0.005; // within half a cent == reconciles (float- + rounding-robust)

// ── types ───────────────────────────────────────────────────────────────────────
export type BreakdownKind =
  | 'operations'
  | 'saatgut_estimate'
  | 'leistung_estimate'
  /** HOF-518 — Fläche x Pachtansatz je Hektar. */
  | 'land_estimate'
  | 'allocations'
  /** HOF-510 — the applications a category-priced line could not price. NOT a derivation. */
  | 'unpriced_posten'
  /**
   * #4172 wave 2 — a labour line's MONTHS. The Einzelposten are one row per month per
   * activity, straight off the fact's own ``labour_posten``, so the Σ is the line by
   * construction and the reconciliation tick is a real check rather than a formality.
   */
  | 'labour_monthly'
  /**
   * Layer C „Investition" — one row per installation class, each carrying its own Rechenweg
   * (Ludger's formula, or a farm pool × this cycle's share). Fact-resident, like the labour
   * months, so the Σ is a real check against ``direct_costs.investition``.
   */
  | 'investition';

/** Whether a cascade line offers a source breakdown, is inert, or is source-less. */
export type LineSelectability = 'selectable' | 'disabled' | 'static';

/**
 * A cross-app deep-link on a Rechenweg factor or an Einzelposten.
 *
 * #2508 — ``label`` is the anchor's own visible text and NAMES ITS DESTINATION (the Kultur
 * whose Bestand it opens, the price it came from, the Buchung it belongs to). It is never
 * the destination's CATEGORY ("im Betrieb" / "im Preisspiegel"), which told the reader
 * nothing about where the click lands.
 */
export interface BreakdownLink {
  /** Route path + query, e.g. ``/farm?lense=operations&item=op-1``. */
  to: string;
  /** Which surface it opens (styling/analytics only — ``label`` carries the meaning). */
  target: 'farm' | 'preisspiegel';
  /**
   * The destination's own identity.
   *
   * ⚠️ Since HOF-21 this is not always the VISIBLE text — see ``bare``. It is always the
   * anchor's ACCESSIBLE NAME, which is why removing a word from the screen must never
   * mean removing it from here.
   */
  label: string;
  /**
   * HOF-21 AC 18 — render the arrow alone, with ``label`` moved into ``aria-label`` and
   * ``title``.
   *
   * The Leistung Posten line reads ``3.048,00 kg × Ø 2,15 €/kg  Pflückung ↗``, and the
   * word „Pflückung" is redundant three times over: the drawer is the Leistung line, the
   * strip already shows a picked Menge, and every row on the panel says it. So the word
   * goes and the affordance stays.
   *
   * **The accessibility trap this flag exists to avoid.** That word IS the anchor's
   * accessible name today — the arrow is ``aria-hidden`` — so deleting it would leave a
   * link with no accessible name at all, which a screen reader announces as "link" and
   * nothing else. Carrying it into ``aria-label``/``title`` is the whole point of routing
   * this through a flag rather than through an empty ``label``.
   */
  bare?: boolean;
}

/**
 * #2508 — one ``Rechenweg`` row: a NAMED driver of the line's derivation, pre-formatted so
 * the render layer stays crop-/line-agnostic. Emitted only for a derived line (today the
 * Saatgut/Pflanzgut estimate); a plain Σ-of-bookings line emits none, so the Rechenweg
 * table simply does not render.
 */
export interface BreakdownFactor {
  /** Stable React key. */
  key: string;
  /** The factor's own name (``Fläche`` / ``Pflanzbestand`` / ``Preis je Pflanze`` / …). */
  label: string;
  /** Pre-formatted value incl. unit; null → ``links`` carry the cell (the value IS the link). */
  value: string | null;
  /** Deep-links naming their own destination; empty when the factor has no addressable source. */
  links: BreakdownLink[];
  /** The value is an estimate, not a booked figure → drives the ``geschätzt`` badge. */
  estimated: boolean;
  /** Hover copy explaining the factor; null when self-explanatory. */
  note: string | null;
}

/** #2508 — the ``Rechenweg``'s closing ``= …`` row (what the factors add up to). */
export interface BreakdownResult {
  label: string;
  /** Pre-formatted € result. */
  value: string;
}

/**
 * #2769-a — where an ``Einzelposten`` row's € comes from.
 *
 * ``own`` — the row's € is its own (a booking on the operations path; the derivation's own
 * result on an estimate line). It was every row's provenance before #2769-a.
 * ``apportioned`` — a SHARE of an undated MyFarm24 Jahreskosten lump, allocated onto a dated
 * Maßnahme so the reader can see WHEN it was spent. It is not a booking and must never be
 * merged into one. Keeping the two as separate rows carrying their own origin IS the product
 * requirement recorded with the option-B decision: "the farmer must see which part of a
 * bucket is booked and which part is an allocation from the undated lump", not an aggregate.
 */
export type BreakdownItemOrigin = 'own' | 'apportioned';

/**
 * #2508 — one ``Einzelposten`` row. The table is always ``Datum | Posten | Betrag``: every
 * link lives INSIDE Posten (no column may sit behind the number) and Betrag is last.
 */
export interface BreakdownItem {
  /** Stable React key. */
  key: string;
  /**
   * HOF-467 follow-up — what KIND of Posten this is, rendered before its identity: for a
   * material input the Preisspiegel Kategorie its product is a tile of (``Fungizide``,
   * ``Mineraldünger`` — the catalog's own name, never a map in this module). Absent on a row
   * that has no kind to state.
   */
  kind?: string | null;
  /** ISO date (``YYYY-MM-DD``) or null — the component formats it. */
  date: string | null;
  /** Posten primary text; ``''`` when the row's identity is carried by its ``links``. */
  label: string;
  /** Secondary descriptor (Basis, Mittel, Methode, Menge) — plain text, no locale formatting. */
  detail: string | null;
  /** The € contribution (magnitude) — the component formats it. */
  amount: number;
  /** #2460 — served quantity for the ``Menge × Ø-Preis`` Posten strip
   *  (price = ``amount``/``qty``; ``amount``/Betrag stays authoritative); null off Leistung. */
  qty: number | null;
  /** #2460 — the quantity's unit (kg/dt), pairing with ``qty``; null off Leistung. */
  unit: string | null;
  /** Deep-links rendered inside Posten, each labelled with its own destination. */
  links: BreakdownLink[];
  /** #2769-a — a booking of its own, or an allocation of an undated lump. */
  origin: BreakdownItemOrigin;
  /** #2769-a — for an ``apportioned`` row: the ``operation_id`` of the lump it came from. */
  originRef: string | null;
  /**
   * #2769-a — the row is not a booked figure → the shared ``geschätzt`` badge, the SAME
   * affordance a Rechenweg factor already uses. A second visual vocabulary for "not a booked
   * number" is exactly the drift ``estimateMark`` exists to prevent.
   */
  estimated: boolean;
  /** Hover copy behind ``estimated``; null when the row is self-explanatory. */
  note: string | null;
  /**
   * HOF-21 AC 19 — the Preisspiegel target for the PRICE in the ``Menge × Ø-Preis`` strip,
   * with the item and month the valuation actually used preselected. Null off Leistung, and
   * on any Posten that carries no Preisspiegel price of its own.
   *
   * Its own field rather than a second entry in ``links``: ``links`` renders after the
   * strip, and this arrow has to sit ON the price it addresses — a reader clicking beside a
   * number must land on that number's own chart cell, not on the row's.
   */
  priceLink: BreakdownLink | null;
  /**
   * HOF-143 — the source of the ``Menge`` in the ``Menge × Ø-Preis`` strip: the Schlagkartei
   * booking the kg were read off. Null when there is no strip for it to sit in (every cost
   * bucket, and a Leistung Posten whose op carries no quantity), where the same link stays
   * in ``links`` and keeps its trailing placement.
   *
   * Its own field for HOF-21 AC 19's reason, applied to the other half of the strip: an
   * entry in ``links`` renders as a joined part AFTER the whole strip, so the quantity's
   * arrow sat a column of glyphs away from the number it explains — a dangling ``· ↗`` a
   * reader cannot associate with anything. Which link is the quantity's source is a fact
   * about the DATA, and the builder is the layer that already knows every link's
   * destination; the renderer only places what it is handed.
   */
  qtyLink: BreakdownLink | null;
  /**
   * HOF-102 — the Ø price in the ``Menge × Ø`` strip, taken OFF THE FACT with its own unit.
   *
   * Not ``|amount| ÷ qty``, which is what this strip used to render. The quotient is only
   * the price when both operands share a unit, and they routinely do not: the worker
   * prices Marktfrüchte in €/t and Kartoffeln in €/dt against a Menge in kg or dt, and
   * ``leistung_from_yield`` owns that conversion precisely so the browser never guesses at
   * it. A derived quotient would print a €/dt price relabelled „€/kg" — the #2695 D 10x
   * defect class, on the number the farmer is looking at.
   *
   * Null on a Posten with no price (and on every cost bucket), where the derived Ø is
   * still the honest thing to show.
   */
  unitPrice: PostenPrice | null;
  /**
   * HOF-102 — this Posten's Menge has no Preisspiegel price, so it contributed **0 €**
   * (D2) → the „nicht bewertet" chip and a dash rather than a Betrag.
   *
   * The inverse of HOF-21's ``valued``, and the inversion is the feature: under one path
   * every priced Posten is a valuation, so marking them all marks nothing. What a reader
   * needs to see is which kilos are missing from the Σ.
   */
  unpriced: boolean;
  /** Hover behind the strip — the month, the item, what the average was made of; or the refusal. */
  postenNote: string | null;
  /**
   * The row is a MONTHLY fold (``YYYY-MM``) of several bookings rather than one booking —
   * today only the Leistung Einzelposten, where a season is 30+ picks and one row per pick
   * buried the number that matters (the month's Ø). ``date`` is null on such a row; the
   * component prints the month. Null on every per-booking row.
   */
  month: string | null;
}

/** A Posten's own Ø price and ITS unit — both off the fact, never re-derived. */
export interface PostenPrice {
  value: number;
  unit: string;
}

/**
 * The default provenance block — spelled once, not per builder, so a new builder cannot
 * quietly mint a row that claims to be an allocation.
 *
 * ``estimated: false`` on the saatgut/Leistung estimate rows is deliberate and NOT an
 * oversight: those rows already carry their "geschätzt / nicht gebucht" claim in their own
 * label and in the Rechenweg factor above them, so setting the flag would put a SECOND badge
 * on one figure — the exact drift ``estimateMark`` exists to prevent. The flag marks a row
 * whose €-nature the row itself does not otherwise state.
 */
/** Hover behind the „nicht bewertet" chip on a booking its line could not price at all. */
const UNPRICED_BOOKING_NOTE = 'Für diese Buchung ist kein Preis hinterlegt — sie steht mit 0,00 € in der Summe.';

const OWN = {
  origin: 'own' as const,
  originRef: null,
  estimated: false,
  note: null,
  priceLink: null,
  qtyLink: null,
  unitPrice: null,
  unpriced: false,
  postenNote: null,
  month: null,
};

export interface SourceBreakdown {
  kind: BreakdownKind;
  /** ``Rechenweg`` factors — EMPTY for a line that is a plain Σ of bookings. */
  factors: BreakdownFactor[];
  /** The Rechenweg's result row; null exactly when ``factors`` is empty. */
  result: BreakdownResult | null;
  /** ``Einzelposten`` rows. */
  items: BreakdownItem[];
  /** ``round2(Σ raw contributions)``. */
  total: number;
  /** The persisted cascade-line magnitude the total must equal. */
  lineValue: number;
  /** ``|total − lineValue| < 0.005`` — the honest reconciliation flag. */
  reconciles: boolean;
  /**
   * HOF-510 — present, and ``false``, exactly when ``items`` are NOT this line's derivation
   * and no reconciliation verdict may be printed about them.
   *
   * Every other breakdown answers „does the Σ of these rows equal the line?", and both
   * answers are meaningful. On a category-priced line whose applications the Preisspiegel
   * could not price, neither is: ``reconciles: false`` would accuse a correct, document-derived
   * line of being wrong, and forcing ``total === lineValue`` would claim a set of 0,00 € rows
   * adds up to it. The rows are context — „these are the applications behind this bucket that
   * carry no price" — so the panel says that instead of grading a sum nobody computed.
   *
   * Optional, and absent on every other kind, so „no flag" keeps meaning what it always did:
   * this breakdown does derive its line, and ``reconciles`` is the verdict.
   */
  derivesTheLine?: false;
}

// ── pure helpers ────────────────────────────────────────────────────────────────
const fin = (v: number | null | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

const reconciles = (total: number, lineValue: number): boolean =>
  Math.abs(total - lineValue) < RECONCILE_TOLERANCE;

/** Port of ``_labour_cost``: explicit non-zero ``cost`` wins, else ``hours×rate``, else ``akkord_rate×kg``. */
export function labourCost(labour: LabourEntry[]): number {
  let total = 0;
  for (const e of labour) {
    const cost = fin(e.cost);
    if (cost) {
      total += Math.abs(cost);
      continue;
    }
    const hours = fin(e.hours);
    const rate = fin(e.rate);
    const akkord = fin(e.akkord_rate);
    const kg = fin(e.kg);
    if (hours !== null && rate !== null) total += Math.abs(hours * rate);
    else if (akkord !== null && kg !== null) total += Math.abs(akkord * kg);
  }
  return total;
}

/** Port of ``_machinery_cost``: explicit non-zero ``cost`` wins, else ``hours×rate``. */
export function machineryCost(machinery: MachineryEntry[]): number {
  let total = 0;
  for (const e of machinery) {
    const cost = fin(e.cost);
    if (cost) {
      total += Math.abs(cost);
      continue;
    }
    const hours = fin(e.hours);
    const rate = fin(e.rate);
    if (hours !== null && rate !== null) total += Math.abs(hours * rate);
  }
  return total;
}

/**
 * The direct-cost bucket an explicit ``ledger_category`` classifies to, or null. Shared
 * by ``costBucket`` (cost routing) and ``bucketRecordCount`` (#2476 record attribution)
 * so the two never diverge on ledger precedence: a direct-cost ledger always wins over
 * the operation ``type``; a mis-set rev_/sub_ ledger yields null (falls back to type).
 */
function ledgerDirectCostBucket(op: OperationInfo): string | null {
  const ledger = op.ledger_category;
  if (!ledger) return null;
  const byLedger = COST_BUCKET_BY_LEDGER[ledger] ?? (ledger.startsWith('exp_') || ledger === 'expense' ? 'sonstige' : null);
  return byLedger && DIRECT_COST_BUCKETS.has(byLedger) ? byLedger : null;
}

/**
 * The direct-cost bucket an operation's material ``cost`` lands in (port of the
 * ``_operation_lines`` cost branch): an explicit ``ledger_category`` that classifies
 * to a direct-cost bucket wins; else the ``type`` bucket (sow/fertilize/spray); else
 * ``sonstige``. A mis-set rev_/sub_ ledger never routes a cost to revenue/subsidy.
 */
export function costBucket(op: OperationInfo): string {
  return ledgerDirectCostBucket(op) ?? OPERATION_TYPE_BUCKET[op.type] ?? 'sonstige';
}

/** A harvest/pick operation — its money is revenue/Leistung, so it is no material-cost record. */
const isPick = (op: OperationInfo): boolean => op.type === 'pick' || op.type === 'harvest';

/**
 * #2476 — the direct-cost bucket an operation's material dimension belongs to, or null
 * when the op carries none (a plain pick/harvest contributes no material record). Same
 * ledger-wins-over-type precedence as ``costBucket`` AND the same ``sonstige`` fallback
 * for a non-harvest field op (e.g. ``irrigate``) — so a recorded-but-priceless such op
 * reads "Preis fehlt", not "Keine Erfassung" — but WITHOUT ``costBucket``'s blanket
 * default, so a priceless pick is not miscounted as a Sonstige record.
 */
function materialCostBucket(op: OperationInfo): string | null {
  const explicit = ledgerDirectCostBucket(op) ?? OPERATION_TYPE_BUCKET[op.type];
  if (explicit) return explicit;
  return isPick(op) ? null : 'sonstige';
}

/** One operation's raw € contribution to ``bucket`` — the sum of its line types that land there. */
function opContribution(op: OperationInfo, bucket: string): number {
  let sum = 0;
  // HOF-102 — ``op.revenue`` is NOT read, on any bucket. It was the allocator's
  // kg-weighted booth-POS share, which W3 severed from the engine: it reached no
  // ``leistung`` on any fact, so a Betrag derived from it here would have been a number
  // with nothing above it to reconcile against. And it was not merely stale — it was the
  // WRONG number by 4,7-17,7x (July 2026 Erdbeeren: 0,30 €/kg booked against 5,31 €/kg
  // realised), which is why the change exists.
  //
  // The temptation was to keep it as a fallback for a row whose fact carries no
  // ``leistung_posten`` block. That is exactly the case where it does most harm: the panel
  // would list Posten summing to the booth pool underneath a ``leistung`` computed without
  // it, reading as the breakdown of a number it has no relation to.
  //
  // HOF-104 closed the door behind that decision: the field is retired at the producer,
  // dropped from the served payload and gone from ``OperationInfo``, so it is not merely
  // unread here — it is unreachable. The Leistung Einzelposten come from the fact's block
  // or not at all.
  const cost = fin(op.cost);
  // HOF-299 — a merged line collects the cost of every bucket it spans.
  if (cost && lineBuckets(bucket).includes(costBucket(op))) sum += Math.abs(cost);
  if (bucket === 'loehne') sum += labourCost(op.labour);
  if (bucket === 'maschinen') sum += machineryCost(op.machinery);
  return sum;
}

const enc = (id: string): string => encodeURIComponent(id);

const farmLink = (path: string, label: string, opts?: { bare?: boolean }): BreakdownLink => ({
  to: path,
  target: 'farm',
  label,
  // Conditional so a non-bare link's object stays byte-identical to the pre-HOF-21 one —
  // ``links`` is compared structurally in more than one test.
  ...(opts?.bare ? { bare: true } : {}),
});

/**
 * #2508 — the Kultur a ``/farm`` crop_cycle anchor opens, used as that link's own text
 * (replacing the category-named "im Betrieb"). Falls back to a neutral destination noun on
 * a fact whose ``crop`` is blank, never to a category label.
 */
const cropLinkLabel = (row: DeckungsbeitragRow): string => row.crop || 'Bestand';

/**
 * #2508/#2538 — the estimated unit price's Preisspiegel anchor target. The app root is now the
 * FALLBACK; ``preisspiegelHref`` below builds the per-item/per-day deep link whenever the fact
 * can address one.
 */
const PREISSPIEGEL_APP = '/apps/preisspiegel';

/** An ISO calendar day — the only shape the Preisspiegel ``?d=`` param honours. */
const ISO_DAY = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/**
 * #2538 — the Preisspiegel target for a price the ``saatgut_estimate`` fact can address:
 * ``?kat=&item=&d=``, else the app root.
 *
 * The worker emits only ``catalog_item_id`` + ``observation_date``; the Kategorie is derived
 * HERE via ``findItem`` because the catalog module is the single authority on which Kategorie
 * an item lives under. Deriving it from ``row.crop`` instead would be a guess — a crop tracked
 * under several items would silently land on the wrong tile, and ``?item=`` outside its
 * ``?kat=`` degrades without any signal.
 *
 * Every gate below is a DEGRADE-to-app-root, never a broken link: a missing/half-formed pair
 * (the materialiser emits both keys or neither), a non-ISO day (``?d=`` would go unobserved and
 * the chart would silently fall through to the latest day), or an item the FE catalog mirror
 * does not know — the mirror-vs-config drift class ``useOwnPriceSeries`` already warns about.
 */
const preisspiegelHref = (
  itemId: string | null | undefined,
  day: string | null | undefined,
  grain?: 'month',
): string => {
  if (!itemId || !day || !ISO_DAY.test(day)) return PREISSPIEGEL_APP;
  const loc = findItem(itemId);
  if (!loc) return PREISSPIEGEL_APP;
  const g = grain ? `&g=${enc(grain)}` : '';
  return `${PREISSPIEGEL_APP}?kat=${enc(loc.kategorie.id)}&item=${enc(itemId)}&d=${enc(day)}${g}`;
};

/**
 * HOF-21 AC 19 — ``YYYY-MM`` → the first of that month, the only shape ``?d=`` honours.
 *
 * A valued Posten's price is a MONTH's weighted average, and the Preisspiegel addresses
 * days. Landing on the first of the month with ``?g=month`` puts the reader on exactly
 * the chart cell that produced the € on the row; landing on the Posten's own day would
 * open a daily point that is not the number they clicked.
 */
const monthAnchorDay = (month: string | null | undefined): string | null =>
  month && /^\d{4}-(0[1-9]|1[0-2])$/.test(month) ? `${month}-01` : null;

/**
 * Estimate basis → the Rechenweg's Bestand factor name, the quantity noun, the Einzelposten
 * detail noun and the €/unit label. Unknown basis → the neutral fallback below.
 */
const SAATGUT_BASIS_META: Record<string, { factor: string; qtyNoun: string; detailNoun: string; unit: string }> = {
  pflanze: { factor: 'Pflanzbestand', qtyNoun: 'Pflanzen', detailNoun: 'Pflanzen', unit: 'Pflanze' },
  gewicht: { factor: 'Saatgutmenge', qtyNoun: 'dt Saatgut', detailNoun: 'Saatgutmenge', unit: 'dt' },
  einheit: { factor: 'Saateinheiten', qtyNoun: 'Saateinheiten', detailNoun: 'Saateinheiten', unit: 'Einheit' },
};
const SAATGUT_BASIS_FALLBACK = {
  factor: 'Bestand',
  qtyNoun: 'Einheiten',
  detailNoun: 'Einheiten',
  unit: 'Einheit',
};

const ESTIMATED_PRICE_NOTE = 'Geschätzter Einstandspreis aus dem Preisspiegel — keine gebuchte Rechnung.';

// ── HOF-518: the Landkosten notes ───────────────────────────────────────────────
// The first is the one sentence a farmer will otherwise get wrong: the charge is on EVERY
// hectare, owned ones included, and that is deliberate — if only rented fields carried Pacht,
// an owned field would look structurally cheaper than an identical rented one and the
// Deckungsbeitrag would measure land TENURE instead of how the crop performed.
const LAND_EVERY_HECTARE_NOTE =
  'Angesetzt wird auf jeden bewirtschafteten Hektar — auch auf eigene Flächen, damit eigene und gepachtete Schläge vergleichbar bleiben.';
const LAND_ESTIMATED_RATE_NOTE =
  'Geschätzter Pachtansatz aus dem Preisspiegel — kein Pachtvertrag hinterlegt. Ein hinterlegter Vertrag ersetzt diesen Wert.';
const LAND_CONTRACT_RATE_NOTE = 'Aus dem hinterlegten Pachtvertrag abgeleitet (Jahrespacht ÷ Fläche).';
// HOF-518 (codex r2) — the multi-lease farm. Its € and its badge are the estimate's, but the
// REASON is the opposite one, and the reason is what tells a farmer whether uploading another
// document would help: here it would not, and „kein Pachtvertrag hinterlegt" would send them
// looking for a Vertrag they already gave us.
// Deliberately does NOT name the grade that stepped in: the leg falls back to whatever the
// tile's next-best grade is, and asserting „Schätzwert" here would contradict the badge beside
// it on the day that grade is not one. What the farmer needs is the same either way — no
// contract rate is being used, and uploading another document will not change that.
const LAND_AMBIGUOUS_CONTRACTS_NOTE =
  'Es liegen mehrere Pachtverträge mit unterschiedlichen Sätzen vor — welcher für welchen Hektar gilt, geht daraus nicht hervor. Die Zeile rechnet deshalb nicht mit einem Vertragssatz.';

// ── the Substrat row under Sonstige (2026-10-06) ───────────────────────────────
// The row's hint and its badge hover. The hint is the one claim a farmer would otherwise get
// wrong: on a Schlag-derived row length the metres were not counted on this cycle, they were
// computed from the Schlag's Reihenabstand — the worker reads that length here on purpose
// (the one declared exception to HOF-312's fence), and the row has to say so.
const SUBSTRAT_ROW_LENGTH_HINT: Record<string, string> = {
  derived_from_field_row_spacing: 'Laufmeter aus dem Reihenabstand des Schlags berechnet',
  derived_from_row_spacing: 'Laufmeter aus dem Reihenabstand berechnet',
  derived_from_tunnels: 'Laufmeter aus den Tunneln des Schlags (Reihen × Länge)',
};
const SUBSTRAT_UNKNOWN_GROW_SYSTEM_NOTE = 'Bei Tunneln ohne Angabe zum Anbausystem wird Substrat angenommen.';
const SUBSTRAT_PRICE_ESTIMATE_NOTE = 'Geschätzter Preis je m³ aus dem Preisspiegel — keine gebuchte Rechnung.';
const SUBSTRAT_RULE_ESTIMATE_NOTE = 'Die Menge je Laufmeter ist ein Richtwert, auf dem Betrieb nicht gemessen.';
const SUBSTRAT_ESTIMATE_FALLBACK_NOTE = 'Geschätzter Wert — kein gebuchter Betrag.';

// ── #2695 E: the modelled-Leistung copy ─────────────────────────────────────────
// Every string here has one job: keep an ESTIMATED revenue figure from reading as money the
// farm received. The Leistung line is the only place in the cascade where that confusion is
// possible, and the row sits in the same table as cycles whose Leistung is real.

/** The Menge really was harvested — only the price is modelled. Says so, so the badge is unambiguous. */
const HARVEST_QTY_NOTE = 'Tatsächlich erfasste Erntemenge aus der Schlagkartei.';

/** The price factor's hover. Carries the "not booked revenue" claim; the book anchor is appended. */
const ESTIMATED_LEISTUNG_PRICE_NOTE =
  'Geschätzter Verkaufspreis aus dem kuratierten Preisbuch — kein gebuchter Erlös.';

/** The Rechenweg's closing row. Names BOTH facts about the €: estimated, and unbooked. */
const ESTIMATED_LEISTUNG_RESULT_LABEL = 'Geschätzte Leistung · kein Erlös gebucht';

/** The single Einzelposten. "nicht gebucht" so the row cannot read as a Buchung. */
const ESTIMATED_LEISTUNG_ITEM_LABEL = 'Geschätzter Erlös — nicht gebucht';
const AMORTISE_NOTE =
  'Etablierungskosten einer Dauerkultur — über die Standjahre verteilt, nicht voll pro Jahr.';

// ── #2608: the ANNUAL half of the Nutzungsdauer axis ────────────────────────────
/**
 * The note behind "Nutzungsdauer: 1 Jahr".
 *
 * This exists because the recurrence LOOKED like a mistake: WF's Himbeeren Lagorai books the
 * full 7.000,00 € plant estimate in every Wirtschaftsjahr and the Rechenweg said nothing
 * about it, so a reader reasonably read a one-off planting cost mis-assigned to every year.
 *
 * The note therefore describes **the booking**, not the plants. That restraint is deliberate
 * and was a review finding: the worker normalises the price book's legacy `amortise` token to
 * `annual` (this engine amortises on `establishment` ALONE, so such a row genuinely books its
 * full gross every year) — which means a fact reading `annual` may describe a row the catalog
 * still marks as a 2-year Minitray/frigo planting. A causal claim like "Bestand wird jährlich
 * ersetzt" would then be flatly contradicted by `seed-price-estimates@v1.yaml` in this same
 * repo, and the FE has no way to tell the two apart — the token cannot carry that distinction
 * by construction. So the copy asserts only what the fact proves: this cost was not spread.
 * It still answers the reader's actual question (why the full amount recurs), without shipping
 * the #2593 defect — a Rechenweg that contradicts its own data — relocated from the € to prose.
 *
 * The basis still picks the NOUN (`Pflanzkosten` vs `Saatgutkosten`), which the fact does prove.
 */
const ANNUAL_NOTE_PFLANZE =
  'Diese Pflanzkosten werden nicht über Standjahre verteilt — der volle Betrag wird in jedem Wirtschaftsjahr gebucht.';
const ANNUAL_NOTE_SAATGUT =
  'Diese Saatgutkosten werden nicht über Standjahre verteilt — der volle Betrag wird in jedem Wirtschaftsjahr gebucht.';

// ── #2593: the stand verdict behind a Saatgut line's € ──────────────────────────
/**
 * The two states whose 0,00 € is the RULE'S VERDICT rather than a missing booking — the
 * mirror of the materialiser's ``_STAND_ZERO_STATES`` (pinned in the mapper tests). Only
 * these change anything here: ``in_window`` / ``unknown`` / ``unknown_year`` all book a real
 * per-year share and must keep rendering byte-identically to a stand-less fact.
 */
const ZERO_STAND_STATES: ReadonlySet<string> = new Set<string>(STAND_ZERO_STATES);

const isZeroStandState = (state: StandState): state is StandZeroState => ZERO_STAND_STATES.has(state);

/**
 * #2593 — the stand block behind a Saatgut line that is 0,00 € BY RULE, or null.
 *
 * The single gate for every #2593 behaviour change (builder, selectability, zero-reason), so
 * the carve-outs can never drift apart: a row without an estimate, without a stand block, or
 * on a value-booking state answers null and takes the pre-#2593 path unchanged. It is also the
 * ONLY way to mint a ``ZeroStateStand``, so the zero-state copy cannot be pointed at a stand
 * that books a real share.
 */
export function zeroStateStand(row: DeckungsbeitragRow): ZeroStateStand | null {
  const stand = row.saatgut_estimate?.stand;
  return stand && isZeroStandState(stand.state) ? (stand as ZeroStateStand) : null;
}

/** #2593 — the short German name of an Anlagejahr's trust tier (rendered inline, in brackets). */
const STAND_PROVENANCE_TIER: Record<StandProvenance, string> = {
  farmer_stated: 'laut Betrieb',
  estimated: 'geschätzt',
  operator_assumed: 'vom Betreuer angenommen',
  unknown: 'Herkunft unbekannt',
};

/**
 * #2593 — the full sentence behind the tier, for the factor's hover note / the token title.
 *
 * Rendered ONLY beside a zero-state verdict, so every sentence must hold in that context: an
 * "ohne Standzeit-Fenster gerechnet" note would flatly contradict the "seit 2020 abgeschrieben"
 * line it hovers (a window was applied — it closed), and would tell a farmer his 0,00 € came
 * from having no window at all.
 */
const STAND_PROVENANCE_NOTE: Record<StandProvenance, string> = {
  farmer_stated: 'Anlagejahr vom Betrieb angegeben.',
  estimated: 'Anlagejahr geschätzt — aus vorhandenen Aufzeichnungen abgeleitet, nicht vom Betrieb bestätigt.',
  operator_assumed:
    'Anlagejahr vom Betreuer angenommen — nicht vom Betrieb bestätigt. Ein falsches Jahr schreibt einen tragenden Bestand zu früh ab.',
  unknown: 'Herkunft des Anlagejahres unbekannt — bitte im Betrieb bestätigen lassen.',
};

/**
 * #2593 — why this stand books 0,00 €, naming the Anlagejahr, its trust tier and the window.
 *
 * ONE source of truth for the copy: the Rechenweg factor and the cascade line's reason token
 * both render this, so the two surfaces can never tell the reader different stories. The year
 * is stringified raw — ``formatNumber`` would render 2010 as "2.010", which is not a year.
 */
export function standReasonText(stand: ZeroStateStand): string {
  const tier = STAND_PROVENANCE_TIER[stand.provenance];
  const start = fin(stand.established_year);
  const life = fin(stand.stand_life_years);
  if (stand.state === 'not_yet_planted') {
    return start === null
      ? `Vor der Pflanzung (${tier}) — in diesem Jahr fällt noch kein Pflanzgut an`
      : `Anlage erst ${start} (${tier}) — vor der Pflanzung fällt kein Pflanzgut an`;
  }
  // fully_depreciated: the window closed at established + life − 1, so the write-off runs
  // from the year after it. ``stand_life_years`` is the RESOLVED span — a ``cleared_year``
  // already won over the nominal life upstream — so the year needs no branch of its own. The
  // Rodung is still NAMED, because otherwise a span the farmer himself shortened reads like a
  // price-book default he had no say in.
  if (start === null) return 'Anlage abgeschrieben (Anlagejahr unbekannt)';
  if (life === null) return `Anlage ${start} (${tier}), abgeschrieben`;
  const cleared = fin(stand.cleared_year);
  const span = cleared !== null ? `${cleared} gerodet, ${formatNumber(life)} Standjahre` : `${formatNumber(life)} Standjahre`;
  return `Anlage ${start} (${tier}), seit ${start + life} abgeschrieben (${span})`;
}

/** #2593 — the hover sentence naming how much this stand's Anlagejahr can be trusted. */
export function standProvenanceNote(provenance: StandProvenance): string {
  return STAND_PROVENANCE_NOTE[provenance];
}

// ── HOF-466: an establishment window that was SIZED but never PLACED ─────────────
/**
 * The Anlagealter value for an establishment leg whose stand is ``unknown``.
 *
 * ``amortise_lifetime_years`` SIZES the window; the stand register's Anlagejahr PLACES it.
 * No ``stand-register@v1.yaml`` is authored for any tenant, so the worker's
 * ``_amortise_over_stand`` takes its ``stand is None`` branch on every establishment leg in
 * production — *"today's exact ``gross / nominal_life``, every year, for ever, labelled
 * ``unknown``"* — and the drawer rendered a confident „Nutzungsdauer (Etablierung): 10
 * Jahre" over it with nothing said about the missing year. Measured on the anchor pilot:
 * 181 of 1.257 ``deckungsbeitrag`` facts carry ``stand.state == "unknown"`` and the SAME
 * 181 carry ``cost_treatment == "establishment"``.
 *
 * Two things the sentence must not do, both of which the zero-state copy beside it does:
 * name a year (there is none — asserting one is the failure ``stand_register`` exists to
 * prevent) and say ``abgeschrieben`` (nothing was written off; the window has no end at
 * all). It states the absence and the consequence, and the hover — the already-written,
 * until-now-unreachable ``unknown`` note — carries the ask.
 */
const UNPLACED_STAND_TEXT = 'Kein Anlagejahr hinterlegt — der Anteil wird in jedem Erntejahr gebucht, ohne Ende';

/**
 * HOF-466 — the stand block behind an establishment share whose window is unplaced, or null.
 *
 * The mirror of ``zeroStateStand()`` and deliberately a SECOND gate rather than a widening
 * of it: ``ZeroStateStand`` is narrowed to the two states whose € is 0,00 € by rule, and the
 * ``Kein Anteil dieses Jahres`` copy keyed to it would be a lie over the real share this
 * state books. BOTH legs are required — a stand is only ``unknown`` because no register
 * placed it, but an ``annual`` leg has no window to place, so disclosing there would be a
 * false alarm on a cost the engine never spread.
 */
export function unplacedEstablishmentStand(row: DeckungsbeitragRow): SaatgutStand | null {
  const est = row.saatgut_estimate;
  const stand = est?.stand;
  if (!stand || stand.state !== 'unknown') return null;
  return est?.cost_treatment === 'establishment' ? stand : null;
}

// ── #2608 (#1646 R5): what the "Etablierung (anteilig)" € is a share OF ─────────
/**
 * The short window suffix for the cascade line's label, or null when the fact says nothing
 * usable. Null is a real outcome, not a fallback: the line must degrade to its pre-#2608
 * bare € rather than assert a window the fact does not carry (every prod fact until the
 * post-deploy re-materialise, and every ``expensed_whole`` fact whose basis IS the absence
 * of a window).
 *
 * Reads ONE authority — the R5 ``establishment_detail`` block, which the worker derived from
 * ``_establishment_end_year``, the SAME helper that produced the € beside it. It is never
 * mixed with the Saatgut estimate's ``amortise_lifetime_years`` or the #2533 stand: those
 * describe the PFLANZGUT price-book span, a different number that would silently contradict
 * this one on a cycle carrying both.
 */
export function establishmentWindowLabel(detail: EstablishmentDetail | null): string | null {
  if (!establishmentWindowIsRenderable(detail)) return null;
  if (detail.basis === 'expensed_whole') return 'voll in diesem Jahr';
  const life = fin(detail.lifetime_years) as number;
  const standYear = fin(detail.stand_year);
  // "Standjahre" (not the bare "Jahre") in BOTH shapes and in the hover — one term for one span.
  return standYear === null
    ? `über ${formatNumber(life)} Standjahre`
    : `Standjahr ${standYear} von ${formatNumber(life)}`;
}

/**
 * #2608 — the ONE predicate deciding whether the window is renderable at all.
 *
 * Both the label and the hover route through it, because a review found they could otherwise
 * drift: a block with a usable ``lifetime_years`` but a mangled ``planted_year`` (a JSONB
 * round-trip losing one field) rendered a label with no tooltip behind it. The doc comment
 * claiming they "cannot drift" is only true if a single gate enforces it — so here it is.
 * ``expensed_whole`` needs no year at all; the amortised shape needs the two fields both
 * sentences name.
 */
function establishmentWindowIsRenderable(
  detail: EstablishmentDetail | null,
): detail is EstablishmentDetail {
  if (detail === null) return false;
  if (detail.basis === 'expensed_whole') return true;
  return fin(detail.lifetime_years) !== null && fin(detail.planted_year) !== null;
}

/**
 * The full hover sentence behind that suffix — same block, same gate, so the label and its
 * tooltip can never tell the reader different stories (the #2593 one-source-of-truth rule).
 * Years are stringified raw: ``formatNumber`` would render 2024 as "2.024".
 */
export function establishmentWindowText(detail: EstablishmentDetail | null): string | null {
  if (!establishmentWindowIsRenderable(detail)) return null;
  if (detail.basis === 'expensed_whole') {
    return 'Kein Pflanzdatum hinterlegt — die Etablierungskosten werden deshalb voll in diesem Wirtschaftsjahr gebucht statt über die Standjahre verteilt.';
  }
  const start = fin(detail.planted_year) as number;
  const life = fin(detail.lifetime_years) as number;
  const end = fin(detail.end_year);
  const window = end === null ? '' : ` (${start}–${end})`;
  const standYear = fin(detail.stand_year);
  const ordinal = standYear === null ? '' : ` Dieses Wirtschaftsjahr ist Standjahr ${standYear}.`;
  return `Pflanzung ${start} — die Etablierungskosten werden über ${formatNumber(life)} Standjahre${window} verteilt, nicht voll im Pflanzjahr gebucht.${ordinal}`;
}

/** The persisted magnitude of a cascade bucket line (what the breakdown must reconcile to). */
export function lineMagnitude(row: DeckungsbeitragRow, key: string): number {
  if (key === 'leistung') return row.leistung;
  if (key === 'gemeinkosten') return row.allocated_overhead;
  const dc = row.direct_costs as unknown as Record<string, number>;
  // HOF-299 — the merged line is the cent-exact Σ of its buckets. ``round2`` because two 2dp
  // buckets can sum to a binary residual (0,1 + 0,2), and this number is what the breakdown's
  // ``round2``-ed total reconciles against AND what the cascade prints.
  const buckets = lineBuckets(key);
  if (buckets.length > 1) return round2(buckets.reduce((sum, b) => sum + (dc[b] ?? 0), 0));
  return dc[key] ?? 0;
}

const labourBasisLabel = (labour: LabourEntry[]): string | null => {
  const hasAkkord = labour.some((e) => fin(e.akkord_rate) !== null && fin(e.kg) !== null);
  const hasHours = labour.some((e) => fin(e.hours) !== null && fin(e.rate) !== null);
  if (hasAkkord) return 'Akkord';
  if (hasHours) return 'Stunden';
  return null;
};

const machineNames = (machinery: MachineryEntry[]): string | null => {
  const names = machinery.map((m) => m.machine).filter((n): n is string => !!n);
  return names.length > 0 ? [...new Set(names)].join(', ') : null;
};

const bucketDetail = (op: OperationInfo, bucket: string): string | null => {
  // HOF-21 AC 17 — the ``leistung`` bucket renders NO secondary descriptor. It used to
  // return ``op.channel``, which prints as ``· wishop:1731051``: a wiberry shop channel
  // token, not information for a farmer. Everything else ``bucketDetail`` returns
  // (Löhne-Basis, Maschinen-Namen, Material) stays exactly as it was.
  //
  // Safe under the channel decision, and not by luck: the valuation blends across
  // channels by design and never read ``op.channel``, so nothing downstream needed this
  // token to be right about a €. (It never was — ``_dominant_channel`` stamped the CROP's
  // top-revenue channel onto every pick of that crop and its own docstring called it
  // "Informational".) HOF-104 retired both the stamp and the function; the field is gone
  // from ``OperationInfo``, so this ``null`` is now the only thing this bucket could return.
  if (bucket === 'leistung') return null;
  if (bucket === 'loehne') return labourBasisLabel(op.labour);
  if (bucket === 'maschinen') return machineNames(op.machinery);
  // #2460 — drop the Dünger/PSM material label ONLY when the op TYPE itself names the
  // material (so ``sourceLabel`` already reads "Düngung"/"Pflanzenschutz" — the label was
  // redundant). A ledger-routed cost on a non-matching type (e.g. a purchase-invoice op, or
  // a pick with an exp_fertilizer override) keeps its material descriptor, else the Posten
  // would blank out behind a generic "Buchung"/"Pflückung" link.
  //
  // HOF-299 — on the merged line the op TYPE names ONE of the two materials, so the descriptor
  // is redundant only when the € is classified to the material the type names. A fertilisation
  // booked to ``exp_cropprotection`` keeps its „Pflanzenschutz" descriptor: under one line the
  // link's „Düngung" would otherwise be the only word on the row, and it would be the wrong one.
  const typeBucket = OPERATION_TYPE_BUCKET[op.type];
  if (MATERIAL_LINES.has(bucket) && typeBucket && lineBuckets(bucket).includes(typeBucket) && costBucket(op) === typeBucket) {
    return null;
  }
  return op.ledger_category ? LEDGER_MATERIAL_LABEL[op.ledger_category] ?? null : null;
};

const byDateThenId = (a: OperationInfo, b: OperationInfo): number => {
  const da = a.date ?? '';
  const db = b.date ?? '';
  if (da !== db) return da.localeCompare(db);
  return a.operation_id.localeCompare(b.operation_id);
};

// ── #2769-a: the allocation rows ────────────────────────────────────────────────
/**
 * The Posten descriptor for an allocated share. Says what it is in four words, and
 * "verteilt" (not "gebucht") so the row cannot read as a booking of its own.
 */
const APPORTIONED_DETAIL = 'Anteil aus den Jahreskosten, flächengewichtet verteilt';

/** The hover behind the ``geschätzt`` badge. Names BOTH facts: allocated, and not booked. */
const APPORTIONED_NOTE =
  'Anteil der undatierten Jahreskosten dieses Bestands, flächengewichtet auf diese Maßnahme verteilt — ' +
  'keine eigene Buchung. Die Summe der Anteile ändert die Zeile nicht.';

/**
 * #2769-a — split ``operations`` into the per-lump allocations of ``bucket``.
 *
 * Returns the allocation rows plus, per lump ``operation_id``, the € to DEDUCT from that
 * lump's own row. Deduct-then-emit is what keeps Σ exact: the allocations do not add money,
 * they re-express money the lump already carries at date grain. That is also why the worker
 * must not pre-sum them server-side — the panel needs both halves to stay separable.
 *
 * Two guards, both degrade to today's rendering rather than to invented arithmetic:
 *   • a share whose ``apportioned_from`` names no operation in this cycle's served list, or
 *     names one that contributes nothing to THIS bucket, is dropped;
 *   • a lump whose shares exceed its own contribution (a stale share left by a later
 *     kosten-only run — the accepted option-B trade-off) keeps its whole row and its shares
 *     are dropped wholesale. A negative "residual" row would be arithmetic nobody can read.
 */
interface SortableRow {
  item: BreakdownItem;
  /**
   * What `byDateThenId` compared: the RAW `operation_id`, `''` for an id-less op.
   *
   * Deliberately NOT `BreakdownItem.key` — that carries a uniqueness suffix, and there is no
   * key spelling that restores the old position for an id-less op: `localeCompare` treats
   * U+0000 as completely **ignorable**, so a NUL-prefixed fallback collates AMONG the real ids
   * exactly as a bare `op-N` would (and `op-10` still lands before `op-2`).
   */
  sortKey: string;
}

function apportionedRows(
  bucket: string,
  operations: OperationInfo[],
): { rows: SortableRow[]; deduct: Map<string, number> } {
  // ``apportioned_cost`` is a COST carrier by definition, and the Leistung bucket is a
  // revenue one — so without this guard a share could deduct from, and re-label, a Leistung
  // row while Σ stayed correct and the ✓ stayed green. Doubly unreachable now (today's
  // worker apportions only sow/fertilize/spray, and since HOF-104 no operation carries a
  // revenue figure at all), but the guard is what says so rather than the caller's luck.
  if (bucket === 'leistung') return { rows: [], deduct: new Map() };
  const contribution = new Map<string, number>();
  for (const op of operations) {
    if (op.operation_id) contribution.set(op.operation_id, opContribution(op, bucket));
  }
  const byLump = new Map<string, OperationInfo[]>();
  for (const op of [...operations].sort(byDateThenId)) {
    const share = fin(op.apportioned_cost);
    const from = op.apportioned_from;
    if (!share || !from || !op.operation_id) continue;
    if (!contribution.get(from)) continue; // unresolvable origin, or a lump of another bucket
    byLump.set(from, [...(byLump.get(from) ?? []), op]);
  }

  const rows: SortableRow[] = [];
  const deduct = new Map<string, number>();
  for (const [lumpId, shares] of byLump) {
    const lumpAmount = contribution.get(lumpId) as number;
    const allocated = shares.reduce((sum, op) => sum + Math.abs(fin(op.apportioned_cost) as number), 0);
    if (allocated > lumpAmount + RECONCILE_TOLERANCE) continue; // stale over-allocation → degrade
    deduct.set(lumpId, allocated);
    for (const op of shares) {
      const opLabel = OP_TYPE_LABEL[op.type] ?? (op.type || 'Buchung');
      const link = op.operation_id ? farmLink(`/farm?lense=operations&item=${enc(op.operation_id)}`, opLabel) : null;
      rows.push({
        // Sorts immediately after its own booked row (`d0` < `d0~apportioned`), so an
        // allocation sits beside the Maßnahme whose date it borrows.
        sortKey: `${op.operation_id}~apportioned`,
        item: {
          key: `${op.operation_id}~apportioned`,
          date: op.date,
          label: link ? '' : opLabel,
          detail: APPORTIONED_DETAIL,
          amount: Math.abs(fin(op.apportioned_cost) as number),
          qty: null,
          unit: null,
          links: link ? [link] : [],
          origin: 'apportioned',
          originRef: lumpId,
          estimated: true,
          note: APPORTIONED_NOTE,
          // An allocated share is a COST row; it has no price and is never a Leistung Posten.
          // No ``Menge × Ø`` strip either (``qty: null``), so its op link keeps the trailing
          // placement every cost row has — HOF-143 moves the arrow only where a Menge is.
          priceLink: null,
          qtyLink: null,
          unitPrice: null,
          unpriced: false,
          postenNote: null,
          month: null,
        },
      });
    }
  }
  return { rows, deduct };
}

// ── HOF-102: the Leistung Einzelposten, off the fact ───────────────────────────
/**
 * The fact's Posten, indexed by ``operation_id``.
 *
 * An index rather than a scan because the panel is per-Posten and the block is per-Posten:
 * the two lists describe the same operations and the join has to be by identity, never by
 * position. A worker that emitted its items in a different order from ``byDateThenId``
 * would otherwise re-price every row against its neighbour's month, silently.
 */
function postenIndex(row: DeckungsbeitragRow, bucket: string): Map<string, LeistungPostenItem> | null {
  if (bucket !== 'leistung' || row.leistung_posten === null) return null;
  return new Map(row.leistung_posten.items.map((item) => [item.operation_id, item]));
}

/**
 * The per-Posten blocks a cascade line reads, in ``lineBuckets`` order.
 *
 * ONE map from bucket to block, because ``inputPostenIndex`` and ``refusedInputPosten`` ask
 * the same question and a second copy is how a newly added bucket reaches one of them and
 * not the other — which is exactly what would have happened to ``maschinen`` (HOF-519).
 */
function postenBlocksOf(row: DeckungsbeitragRow, bucket: string): InputPostenBlock[] {
  return lineBuckets(bucket)
    .map((b) =>
      b === 'duenger'
        ? row.duenger_posten
        : b === 'psm'
          ? row.psm_posten
          : // HOF-519 — the machinery passes behind the Maschinen line: one Posten per pass,
            // the machine as its article and the pass's own area as its Menge.
            b === 'maschinen'
            ? row.maschinen_posten
            : null,
    )
    .filter((b): b is InputPostenBlock => b !== null);
}

/**
 * HOF-307 — the material-input Posten behind a line, by operation. The merged
 * ``duenger_psm`` line reads BOTH blocks (``lineBuckets``), a single bucket its own; a line
 * with no block at all answers null and the builder keeps its per-operation rows.
 */
function inputPostenIndex(row: DeckungsbeitragRow, bucket: string): Map<string, InputPostenItem[]> | null {
  const blocks = postenBlocksOf(row, bucket);
  if (blocks.length === 0) return null;
  const index = new Map<string, InputPostenItem[]>();
  for (const block of blocks) {
    for (const item of block.items) {
      const list = index.get(item.operation_id);
      if (list) list.push(item);
      else index.set(item.operation_id, [item]);
    }
  }
  return index;
}

/**
 * HOF-510 — the input Posten of ``bucket`` the Preisspiegel could not price, in fact order.
 *
 * Reads the same blocks ``inputPostenIndex`` does (so the merged ``duenger_psm`` line sees
 * both), but keyed by nothing: these rows exist precisely because no per-operation € was
 * produced for them, so grouping them by operation would be grouping by the thing that is
 * missing.
 */
function refusedInputPosten(row: DeckungsbeitragRow, bucket: string): InputPostenItem[] {
  return postenBlocksOf(row, bucket).flatMap((block) => block.items.filter((item) => !item.valued));
}

/**
 * HOF-510 — whether ``bucket`` is a line the CATEGORY aggregate priced while the cascade
 * refused the applications behind it.
 *
 * Three clauses, and each one is load-bearing:
 *
 * * ``source !== 'operations'`` — on the operations path the Σ-of-bookings builder is right
 *   and already carries the unpriced rows beside the priced ones (the HOF-467 follow-up);
 * * a NON-ZERO magnitude — at exactly 0,00 € the „Preis fehlt" carve-out already opens the
 *   line and renders the full Σ, which is exact there because both sides are zero;
 * * at least one refusal — a fully priced block on a fallback line has no gap to report, and
 *   opening it would be an empty accusation.
 */
function categoryPricedWithRefusals(row: DeckungsbeitragRow, bucket: string): boolean {
  if (row.source === 'operations' || lineMagnitude(row, bucket) <= 0) return false;
  const refusedBuckets = lineBuckets(bucket).filter(
    (member) => refusedInputPosten(row, member).length > 0,
  );
  // codex r1, P2 — asked PER MEMBER, because the merged ``duenger_psm`` line is a SUM and the
  // note this unlocks claims the line's amount is complete. With ``duenger`` 150,00 € from
  // invoices and ``psm`` 0,00 € with refusals, the merged magnitude is non-zero while the PSM
  // applications really are missing from it — the worker says so too, flagging
  // ``psm_incomplete`` on its own zero bucket. Telling the farmer that line is „vollständig"
  // would be exactly the false claim this whole change removes, one level up.
  //
  // A member that refused nothing is not consulted: it has no gap to be short by.
  return refusedBuckets.length > 0 && refusedBuckets.every((member) => lineMagnitude(row, member) > 0);
}

/**
 * HOF-510 — the applications behind a category-priced line that carry no price.
 *
 * Deliberately NOT a Σ. ``total`` is 0 because that is what these rows contribute, and
 * ``derivesTheLine: false`` is what stops the panel grading 0 against the line and printing
 * „Σ stimmt nicht" about a number the invoices got right. The rows carry the „nicht bewertet"
 * chip and no ``priceLink``, for the same reason an unpriced row does everywhere else: there
 * is no Preisspiegel cell behind them, and an arrow to one would be a false-provenance claim.
 */
function buildUnpricedPostenBreakdown(
  row: DeckungsbeitragRow,
  bucket: string,
  operations: OperationInfo[],
): SourceBreakdown {
  const byId = new Map(operations.map((op) => [op.operation_id, op]));
  const items: BreakdownItem[] = refusedInputPosten(row, bucket).map((posten, index) => {
    const line = materialFromPosten(posten);
    const op = byId.get(posten.operation_id);
    const opLabel = op ? (OP_TYPE_LABEL[op.type] ?? (op.type || 'Buchung')) : null;
    return {
      key: `${posten.operation_id || 'posten'}:${index}`,
      date: op?.date ?? null,
      kind: kategorieLabel(line.kategorieId),
      label: line.article,
      detail: null,
      amount: 0,
      qty: line.qty,
      unit: line.unit,
      // The booking, when this app was served it — so a farmer can open the application the
      // row is about. An unserved Posten keeps its article as its whole identity rather than
      // borrowing a link to a record this app cannot show.
      links: op && posten.operation_id && opLabel ? [farmLink(`/farm?lense=operations&item=${enc(posten.operation_id)}`, opLabel)] : [],
      ...OWN,
      unpriced: true,
      postenNote: line.postenNote,
    };
  });
  // The pooled Umlage rides along (codex r1 on HOF-519). It is the one row on this line that
  // DID land money, and it is the whole of the WF Brombeeren Maschinen line; the Σ builder has
  // always rendered it, and a bucket only reaches this builder once it has a Posten block, so
  // without this the Umlage would vanish exactly when the passes beside it became nameable.
  // Its € is deliberately NOT added to ``total``: this breakdown declares itself a
  // non-derivation, and a partial Σ is the „Σ stimmt nicht" that declaration exists to avoid.
  const pooled = pooledHaRow(row, bucket);
  if (pooled) items.push(pooled);
  // …and the Substrat charge, on the same terms: it landed money, and its € stays out of
  // ``total`` for the same non-derivation reason.
  const substrat = substratRow(row, bucket);
  if (substrat) items.push(substrat);
  return {
    kind: 'unpriced_posten',
    factors: [],
    result: null,
    items,
    total: 0,
    lineValue: lineMagnitude(row, bucket),
    reconciles: false,
    derivesTheLine: false,
  };
}

/**
 * HOF-307 — the Preisspiegel target of an input's price: the article's OWN product tile.
 *
 * Built from the Posten's ``kategorie_id`` rather than through ``findItem``, because a
 * product tile is DERIVED (HOF-299 S3): the compiled catalog does not know it, and the
 * drawer holds no served roster to resolve it against. The day is the price's OWN and only
 * ever its own — the observed month's first day or the list price's Erntejahr — so the link
 * lands on the cell that produced the € on this row. A Posten that names neither, and one
 * that states no price at all, gets no link rather than a borrowed day (HOF-299 S4).
 */
function inputPriceLink(item: InputPostenItem): BreakdownLink | null {
  if (!item.valued || !item.product_id || !item.kategorie_id) return null;
  // HOF-299 S4 — a Posten that states no PRICE gets no arrow. A ``booked`` cost a document
  // booked carries no rate, and the day would then fall through to the operation's own date:
  // an arrow claiming a Preisspiegel cell produced a € it never saw, on a row that shows no
  // price to compare it against. That is the false-provenance claim ``postenPriceLink``
  // refuses a curated estimate for, and it is refused here on the same grounds. Both halves,
  // because the renderer needs both to print the Ø the arrow is supposed to explain.
  if (item.unit_price === undefined || item.price_unit === undefined) return null;
  // The price must NAME its own cell. There is no fall-back to the operation's date: the
  // worker refuses to guess an Erntejahr (``erntejahr=None`` skips the list-price tier and
  // leaves a booked rate with no year), so a Posten can carry a real price and still name no
  // cell — and substituting the operation's own date there is the identical false-provenance
  // claim the refusal above exists to prevent, reintroduced one line later. Found by codex.
  // HOF-509 / codex r1, P1 — the observation's OWN day when the fact states one. Deriving
  // ``<price_year>-01-01`` is exact for a MyFarm24 list quote, which IS published on the
  // annual grain, and wrong for a curated reference price, which UDB dates by its ``as_of``
  // (WF's rows are 2026-09-07). That derivation therefore opened an empty January cell on
  // exactly the Posten the curated grade exists to make correctable. The derivation stays as
  // the FALLBACK, because a fact written before HOF-509 carries no ``price_date``.
  const day = item.price_month
    ? `${item.price_month}-01`
    : item.price_date
      ? item.price_date
      : item.price_year !== undefined
        ? `${item.price_year}-01-01`
        : null;
  if (!day || !ISO_DAY.test(day)) return null;
  const grain = item.price_month ? '&g=month' : '';
  return {
    to: `${PREISSPIEGEL_APP}?kat=${enc(item.kategorie_id)}&item=${enc(item.product_id)}&d=${enc(day)}${grain}`,
    target: 'preisspiegel',
    label: `Preisspiegel: ${item.article}${item.price_month ? ` ${item.price_month}` : ''}`,
    bare: true,
  };
}

// ── HOF-467 follow-up: ONE material row, two sources ──────────────────────────
/**
 * One material input behind a cost line, source-agnostic: what the row prints is decided
 * here once, whether the numbers came off the fact's valued Posten (HOF-307) or off the
 * operation's own booking. The fact wins when it carries a Posten for the operation.
 */
interface MaterialLine {
  article: string;
  /** The material's Preisspiegel Kategorie id — its KIND; null when the source carries none. */
  kategorieId: string | null;
  qty: number | null;
  unit: string | null;
  /** Euro magnitude; 0 on a refused input. */
  amount: number;
  unitPrice: PostenPrice | null;
  priceLink: BreakdownLink | null;
  valued: boolean;
  estimated: boolean;
  /** Hover behind the ``geschätzt`` badge. */
  note: string | null;
  /** Hover on the Menge × Ø strip; null → the renderer's derived-average sentence. */
  postenNote: string | null;
}

/** The fact's valued Posten (HOF-307): the € and the price are the valuation's, with their provenance. */
function materialFromPosten(item: InputPostenItem): MaterialLine {
  return {
    article: item.article,
    kategorieId: item.kategorie_id,
    qty: item.quantity !== null && item.quantity > 0 ? item.quantity : null,
    unit: item.quantity_unit,
    amount: item.amount,
    unitPrice:
      item.unit_price !== undefined && item.price_unit !== undefined ? { value: item.unit_price, unit: item.price_unit } : null,
    priceLink: inputPriceLink(item),
    valued: item.valued,
    estimated: item.estimate,
    note: item.estimate && item.source ? `Richtpreis — Quelle: ${item.source}` : null,
    postenNote: inputPostenNote(item),
  };
}

/**
 * The operation's own booking: the article, its Menge in its own unit and the € the ingest
 * booked (``inputs[].cost`` — Artikel × Menge × Listenpreis, per the ontology). The Ø is that
 * € over that Menge, honest because both live on the same booking in the same unit; there is
 * no Preisspiegel valuation behind it, so no price link and no valuation note. No kind either:
 * the Kategorie is a property of the PRODUCT the fact's valuation resolves, and this row exists
 * precisely because the fact has not been re-materialised with one yet.
 */
function materialFromInput(input: OperationInput, cost: number): MaterialLine {
  const qty = input.menge !== null && input.menge > 0 ? input.menge : null;
  const unit = input.unit && input.unit.trim() !== '' ? input.unit : null;
  return {
    article: input.article,
    kategorieId: null,
    qty,
    unit,
    amount: Math.abs(cost),
    unitPrice: qty !== null && unit !== null ? { value: round2(Math.abs(cost) / qty), unit: `€/${postenUnitLabel(unit)}` } : null,
    priceLink: null,
    valued: true,
    estimated: false,
    note: null,
    postenNote: null,
  };
}

/**
 * The material lines of one operation on ``bucket``, or null when the operation is to keep
 * its single per-operation row: no fact Posten and no inputs; inputs whose € the ingest did
 * not book; inputs whose own € do not add up to what the operation booked (never an invented
 * split); or material that lands in another line than this one.
 */
function materialLines(
  op: OperationInfo,
  bucket: string,
  posten: InputPostenItem[] | undefined,
): MaterialLine[] | null {
  if (posten && posten.length > 0) return posten.map((item) => materialFromPosten(item));
  const inputs = op.inputs.filter((input) => input.article !== '');
  if (inputs.length === 0) return null;
  const cost = fin(op.cost);
  if (!cost || !lineBuckets(bucket).includes(costBucket(op))) return null;
  const costs: number[] = [];
  for (const input of inputs) {
    const c = fin(input.cost);
    if (c === null) return null;
    costs.push(c);
  }
  const booked = costs.reduce((sum, c) => sum + Math.abs(c), 0);
  if (!reconciles(booked, Math.abs(cost))) return null;
  return inputs.map((input, i) => materialFromInput(input, costs[i]));
}

/** The catalog's own name for a material's Kategorie — the row's kind; null when unknown. */
const kategorieLabel = (kategorieId: string | null): string | null =>
  kategorieId ? (findKategorie(kategorieId)?.name ?? null) : null;

/**
 * One ``Einzelposten`` row for a material line. The ARTICLE is the row's identity and the
 * Maßnahme is the arrow beside its Menge (HOF-143) — named for a screen reader (HOF-21 AC 18),
 * wordless on screen because the article already says what the row is. Without a Menge to sit
 * beside, the Maßnahme keeps its named trailing link.
 */
function materialItem(op: OperationInfo, opLabel: string, line: MaterialLine, index: number, ordinal: number): BreakdownItem {
  const link = op.operation_id
    ? farmLink(`/farm?lense=operations&item=${enc(op.operation_id)}`, opLabel, { bare: line.qty !== null })
    : null;
  const qtyLink = link && line.qty !== null ? link : null;
  return {
    key: `${op.operation_id || `op-${ordinal}`}:${index}`,
    date: op.date,
    kind: kategorieLabel(line.kategorieId),
    label: line.article,
    detail: null,
    amount: line.amount,
    qty: line.qty,
    unit: line.unit,
    links: link && !qtyLink ? [link] : [],
    ...OWN,
    estimated: line.estimated,
    note: line.note,
    priceLink: line.priceLink,
    qtyLink,
    unitPrice: line.unitPrice,
    unpriced: !line.valued,
    postenNote: line.postenNote,
  };
}

/** The articles a booking carries that could not be itemised — ``PROBLAD 2,93 l, SpinTor 0,2 l``. */
function inputsDetail(op: OperationInfo): string | null {
  const parts = op.inputs
    .filter((input) => input.article !== '')
    .map((input) =>
      input.menge !== null && input.menge > 0
        ? `${input.article} ${input.unit ? formatPostenQty(input.menge, input.unit) : formatNumber(input.menge)}`
        : input.article,
    );
  return parts.length > 0 ? parts.join(', ') : null;
}

/**
 * HOF-21 AC 19 — the Preisspiegel target for a priced Posten's own price.
 *
 * ``?g=month`` and the first of the PRICE's month, not the Posten's: under a carry-forward
 * those differ, and the link must land on the cell that produced the € on this row. An
 * UNPRICED Posten gets no link — it has no € and no cell, and pointing it somewhere would
 * claim a provenance it does not have.
 *
 * A CURATED-ESTIMATE Posten gets no link either, on the same principle. When no monthly
 * cell exists inside the carry-forward bound the worker falls through to the curated book
 * (``basis: 'estimate'``) and leaves ``price_month`` unset — so ``?? posten.month`` used
 * to substitute the Posten's OWN month and send the reader to a chart cell that did not
 * produce the price beside the arrow. That is the identical false-provenance claim the
 * refusal above exists to prevent, so ``price_month`` is required rather than defaulted:
 * the link is a statement about where the € came from, and an estimate cannot make it.
 */
function postenPriceLink(posten: LeistungPostenItem): BreakdownLink | null {
  if (!posten.valued || !posten.catalog_item_id || !posten.price_month) return null;
  const day = monthAnchorDay(posten.price_month);
  const href = preisspiegelHref(posten.catalog_item_id, day, 'month');
  if (href === PREISSPIEGEL_APP) return null;
  return {
    to: href,
    target: 'preisspiegel',
    // The anchor's accessible name — the destination's own identity, per #2508. Rendered
    // as a bare arrow beside the price it addresses, so the name lives in ``aria-label``.
    label: `Preisspiegel: ${posten.catalog_item_id} ${posten.price_month}`,
    bare: true,
  };
}

/**
 * HOF-102 — the ``sales_commission`` / ``sales_discount`` row.
 *
 * The valuation replaces the GROSS and the deduction is applied after it, so ``Σ Posten``
 * exceeds ``leistung`` by exactly this — and a deduction has no operation of its own to
 * appear as. Without the row the panel's ✓ would read "Σ stimmt nicht" on every cycle
 * that sells through a commissioned channel, which is a false accusation against data that
 * is entirely correct.
 *
 * Undated on purpose (it is a fold of however many lines), and therefore appended AFTER
 * the chronological sort rather than carrying a ``sortKey`` — a null date sorts to the top,
 * where a negative correction to a Σ nobody has read yet makes no sense.
 */
function deductionRow(row: DeckungsbeitragRow): BreakdownItem | null {
  if (row.leistung_deductions <= 0) return null;
  return {
    key: 'leistung-deductions',
    date: null,
    label: 'Abzüge (Provision / Rabatt)',
    detail: 'Vom Abnehmer einbehalten — bereits in der Leistung oben abgezogen',
    amount: -row.leistung_deductions,
    qty: null,
    unit: null,
    links: [],
    ...OWN,
  };
}

/**
 * The German plural of an op-type label for the fold's „N Pflückungen" count. The labels
 * this reaches are ``OP_TYPE_LABEL``'s: the ``-ung`` nouns (Pflückung, Düngung, Buchung)
 * take ``-en``; ``Ernte`` takes ``-n``; anything else is counted with a multiplication sign
 * rather than guessed at.
 */
const countedLabel = (label: string, n: number): string => {
  if (n === 1) return `1 ${label}`;
  if (label.endsWith('ung')) return `${n} ${label}en`;
  if (label.endsWith('e')) return `${n} ${label}n`;
  return `${n}× ${label}`;
};

/**
 * The Leistung Einzelposten, folded to ONE row per month and per valuation.
 *
 * A season on a tunnel is 30+ picks, and the drawer listed each one — ``05.05. 3.645 kg ↗
 * × Ø 5,59 €/kg ↗`` thirty times, with the month's Ø repeating down the column because it
 * IS a monthly average (``LeistungPostenItem.price_month``). The number a farmer reads the
 * panel for is that Ø and the month's Menge behind it, so the rows are now the months.
 *
 * What a fold keeps and what it drops, and why:
 *
 * * **Grouping is by month AND by what the Posten was valued with** (price + unit, the
 *   price's chart cell, and whether it was priced at all). Within a month every pick shares
 *   the fact's Ø, so this is one row per month in the common case; a pick priced with a
 *   carried-forward month (a different Ø and a different cell) or a refused Posten (0 €,
 *   „nicht bewertet") stays its own row inside the month. Averaging across them would
 *   re-derive a price in the browser, which is exactly what HOF-102 forbade.
 * * **The Menge's Schlagkartei link (HOF-143 ``qtyLink``) is dropped, not moved.** A month
 *   row has no single booking to open. The count of folded picks takes its place as the
 *   Posten secondary (or as the primary when there is no Menge to render a strip for).
 * * **The price's Preisspiegel link (HOF-21 AC 19 ``priceLink``) stays.** It is identical
 *   across the group by construction of the key, and it still names the number's source.
 * * **Σ is invariant.** Each row's Betrag is the cent-rounded Σ of its picks' fact €; the
 *   builder's ``total``/``reconciles`` are computed from the raw contributions before the
 *   fold and do not change. Undated rows (none on Leistung today, but the contract allows
 *   them) pass through unfolded in their position.
 *
 * Input rows arrive date-sorted, so months come out chronological.
 */
export function foldLeistungByMonth(items: BreakdownItem[]): BreakdownItem[] {
  const out: BreakdownItem[] = [];
  const groups = new Map<string, { month: string; rows: BreakdownItem[]; slot: number }>();
  for (const item of items) {
    const month = item.date && /^\d{4}-\d{2}/.test(item.date) ? item.date.slice(0, 7) : null;
    if (!month) {
      out.push(item);
      continue;
    }
    const key = [
      month,
      item.unpriced ? 'u' : 'p',
      item.unitPrice ? `${item.unitPrice.value}|${item.unitPrice.unit}` : '',
      item.priceLink?.to ?? '',
    ].join('#');
    const group = groups.get(key);
    if (group) {
      group.rows.push(item);
    } else {
      groups.set(key, { month, rows: [item], slot: out.length });
      out.push(item); // placeholder, replaced below so the month keeps its chronological slot
    }
  }
  for (const { month, rows, slot } of groups.values()) {
    const first = rows[0];
    const qtyRows = rows.filter((r) => r.qty !== null && r.qty > 0);
    const qty = qtyRows.length > 0 ? qtyRows.reduce((sum, r) => sum + (r.qty as number), 0) : null;
    const opLabel = first.qtyLink?.label ?? first.links[0]?.label ?? (first.label || 'Buchung');
    const count = countedLabel(opLabel, rows.length);
    out[slot] = {
      key: `leistung-month:${month}:${slot}`,
      date: null,
      month,
      // HOF-512 — the count is NOT secondary text under the strip any more. „10.404 kg ×
      // Ø 5,59 €/kg · 6 Pflückungen" reads as though the six were a factor of the amount,
      // and they are not: the row's € is Menge × Ø-Preis, and how many trips it took to
      // pick that Menge changes nothing about it. The season's pick count is a real fact
      // and it still has a home — „Ernte: 34 Pflückungen zwischen 05.05. und 07.09." under
      // the panel — where it is one number for the cycle instead of a per-row aside.
      //
      // Without a strip it stays: a row with no Menge and no label has nothing left to
      // identify it, and an anonymous row is worse than a redundant one.
      label: qty ? '' : count,
      detail: null,
      amount: round2(rows.reduce((sum, r) => sum + r.amount, 0)),
      qty,
      unit: qtyRows.find((r) => r.unit)?.unit ?? first.unit,
      links: [],
      origin: 'own',
      originRef: null,
      estimated: rows.some((r) => r.estimated),
      note: rows.find((r) => r.note)?.note ?? null,
      priceLink: first.priceLink,
      qtyLink: null,
      unitPrice: first.unitPrice,
      unpriced: first.unpriced,
      postenNote: first.postenNote,
    };
  }
  return out;
}

// ── breakdown builders ──────────────────────────────────────────────────────────
function buildOperationsBreakdown(row: DeckungsbeitragRow, bucket: string, operations: OperationInfo[]): SourceBreakdown {
  // HOF-102 — one line value, always the fact's own. HOF-21 substituted the block's
  // parallel ``leistung`` here because the page could be in the other mode; there is no
  // other mode and no parallel figure, so re-deriving one would be a second cascade.
  const posten = postenIndex(row, bucket);
  const inputPosten = inputPostenIndex(row, bucket);
  const lineValue = lineMagnitude(row, bucket);
  // #2769-a — computed FIRST: an allocated share re-expresses part of a lump's own €, so the
  // lump's row must shed exactly what the allocation rows carry. Nothing is added; the Σ
  // below is unchanged by construction, which is what keeps the ✓ honest.
  const { rows: allocations, deduct } = apportionedRows(bucket, operations);
  const rows: SortableRow[] = [];
  let sumRaw = 0;
  for (const op of [...operations].sort(byDateThenId)) {
    const deducted = deduct.get(op.operation_id) ?? 0;
    // HOF-307 / HOF-467 follow-up — an operation's MATERIAL is listed per input, „Datum |
    // <Kind> · <Artikel> · Menge ↗ × Ø €/Einheit ↗ | Betrag", from whichever source carries it:
    // the fact's valued Posten (the € off the FACT, the Preisspiegel price with its own link,
    // an unpriced one marked „Preis fehlt" at 0,00 € rather than dropped or shared) or, on a
    // fact that carries no Posten yet, the operation's own booking. A lump whose shares were
    // re-expressed at date grain (#2769-a) keeps its single deducted row.
    const material = deducted ? null : materialLines(op, bucket, inputPosten?.get(op.operation_id));
    if (material) {
      const opLabelForInputs = OP_TYPE_LABEL[op.type] ?? (op.type || 'Buchung');
      material.forEach((line, index) => {
        sumRaw += line.amount;
        rows.push({ sortKey: op.operation_id, item: materialItem(op, opLabelForInputs, line, index, rows.length) });
      });
      continue;
    }
    // ROUNDED, and ONLY on the deducted branch. ``_split_shares`` is Σ-exact in decimal
    // CENTS, not in binary floats: 0,30 € split 1:2 yields 0,10 + 0,20, whose double sum is
    // 0.30000000000000004 — so a FULLY apportioned lump keeps a −5,6e−17 residual, `=== 0`
    // does not catch it, and (being undated) it sorts to the TOP of the panel as a phantom
    // "−0,00 €" row. Measured at ~29 % of WF-shaped lumps. The un-deducted branch stays
    // un-rounded so the pre-#2769-a "sum raw, round once" contract is byte-identical.
    //
    // Rounding is lossless HERE because every deductable contribution is a lump's
    // ``Math.abs(cost)``, which the worker already rounds to the cent. It would NOT be on a
    // ``loehne``/``maschinen`` row, where ``opContribution`` derives ``hours × rate`` and can
    // carry >2dp — a deduct there could shave up to half a cent off ``sumRaw``, i.e. exactly
    // ``RECONCILE_TOLERANCE``, and flip the ✓ false. Unreachable while the worker apportions
    // only sow/fertilize/spray, but that is a property of the DATA, not of this line.
    // HOF-102 — on the Leistung bucket the Posten's € comes from the FACT, taken verbatim.
    // Never re-multiplied here: re-deriving it would re-implement the worker's dt-t
    // conversion in the browser, and a drawer that re-derives can disagree with the DB1
    // its own line is built on. ``opContribution`` reads ``op.revenue`` on no bucket —
    // W3 severed it and HOF-104 retired the field outright.
    const item = posten?.get(op.operation_id);
    const amount = item
      ? item.amount
      : deducted
        ? round2(opContribution(op, bucket) - deducted)
        : opContribution(op, bucket);
    // An UNPRICED Posten is 0 € and stays in the panel anyway (D2): dropping it would hide
    // exactly which kilos are missing from the Σ above, and a farmer who cannot see that
    // cannot judge the number.
    //
    // HOF-467 follow-up — and so does a money-less BOOKING on a line that is itself 0,00 €.
    // „a zero-€ cost booking has nothing to say" (the old rule here) is true only while the
    // line has other money to explain; when the whole line is „Preis fehlt · N Buchungen",
    // those N bookings are the only thing it HAS to say, and skipping them opened an empty
    // panel. Scoped to ``lineValue === 0`` so every other line keeps the old rule exactly.
    const servesEmptyLine = amount === 0 && !item && lineValue === 0 && opServesBucket(op, bucket);
    if (amount === 0 && !item && !servesEmptyLine) continue;
    sumRaw += amount;
    const opLabel = OP_TYPE_LABEL[op.type] ?? (op.type || 'Buchung');
    const link = op.operation_id
      ? farmLink(`/farm?lense=operations&item=${enc(op.operation_id)}`, opLabel, {
          // HOF-21 AC 18 — the LEISTUNG bucket alone drops the word and keeps the arrow.
          // The cost buckets keep theirs: „Düngung" / „Pflanzenschutz" on a cost row is the
          // only thing naming the Maßnahme, whereas on the Leistung line „Pflückung" is
          // said by the drawer, by the Menge strip and by every sibling row.
          bare: bucket === 'leistung',
        })
      : null;
    // #2460 — qty/unit drive the Leistung ``Menge kg × Ø-Preis`` strip only. The QUANTITY
    // still comes from the operation rather than the Posten: it is the same number (the
    // worker read it off this op) and the cost buckets share the code path.
    const qty = bucket === 'leistung' ? fin(op.qty) : null;
    // HOF-143 — that same op link IS the quantity's source: it opens the Schlagkartei
    // booking the kg were read off, which is what makes it belong beside the Menge instead
    // of trailing the whole cell as an unattached ``· ↗``.
    //
    // Conditional on the Menge actually being there, and that is not renderer-coupling but
    // the field's own definition: with no quantity there is no quantity to source. It also
    // guards the one shape that would blank a cell — the strip renders for ``qty > 0`` and
    // falls back to ``label``, which is ``''`` precisely BECAUSE the op is linkable, so a
    // link named here and dropped from ``links`` on a qty-less row would leave the row with
    // no identity at all.
    const qtyLink = link && qty != null && qty > 0 ? link : null;
    rows.push({
      sortKey: op.operation_id,
      item: {
        key: op.operation_id || `op-${rows.length}`,
        date: op.date,
        // #2460/#2508 — the op's own type IS its link text, and the link lives inside Posten.
        // An unlinkable op (no operation_id) keeps that identity as the plain Posten primary,
        // so the cell never blanks out.
        label: link ? '' : opLabel,
        // the articles an un-itemisable booking carries still identify the record (HOF-467 follow-up)
        detail: bucketDetail(op, bucket) ?? inputsDetail(op),
        amount,
        qty,
        unit: bucket === 'leistung' ? op.unit : null,
        // HOF-143 — a link that moved into the strip must not ALSO trail it; that double
        // placement is the „· ↗" this issue removes. Any FURTHER link a builder adds keeps
        // today's trailing ``LinkParts`` placement with its own named destination.
        links: link && !qtyLink ? [link] : [],
        ...OWN,
        qtyLink,
        // HOF-102 — the fact's own annotations, added only when this op HAS a Posten.
        // A cost row keeps the exact pre-HOF-21 shape.
        ...(item
          ? {
              priceLink: postenPriceLink(item),
              unitPrice:
                item.unit_price !== undefined && item.price_unit !== undefined
                  ? { value: item.unit_price, unit: item.price_unit }
                  : null,
              unpriced: !item.valued,
              postenNote: postenNote(item),
            }
          : {}),
        // HOF-467 follow-up — a booking kept ONLY because its whole line reads „Preis fehlt".
        // It carries no €, so it takes the „nicht bewertet" chip and a dash: rendering
        // ``0,00 €`` here would read as a measured zero — the farm spent nothing — when what
        // it means is that nobody could price it. No ``priceLink`` either: there is no price,
        // so there is no Preisspiegel cell to point at, and an arrow to one would be the
        // false-provenance claim ``inputPriceLink`` refuses on the material line.
        ...(servesEmptyLine
          ? { unpriced: true, priceLink: null, unitPrice: null, postenNote: UNPRICED_BOOKING_NOTE }
          : {}),
      },
    });
  }
  for (const allocation of allocations) {
    sumRaw += allocation.item.amount;
    rows.push(allocation);
  }
  // One table, sorted once — an allocation is a dated row like any other and belongs in the
  // chronology, not in a second table below it. Byte-identical to the pre-#2769-a
  // ``byDateThenId`` order on a cycle with no allocations, because ``sortKey`` IS the raw
  // ``operation_id`` that comparator compared — including the ``''`` an id-less op sorts on.
  rows.sort((a, b) => (a.item.date ?? '').localeCompare(b.item.date ?? '') || a.sortKey.localeCompare(b.sortKey));
  // The Leistung Einzelposten are read per MONTH (see ``foldLeistungByMonth``); every cost
  // bucket keeps one row per booking, because there the booking is the thing to open.
  const perOp = rows.map((r) => r.item);
  const items = bucket === 'leistung' ? foldLeistungByMonth(perOp) : perOp;
  // HOF-102 — appended after the sort, and last: it is a correction to the Σ above it, so
  // it reads where a reader has already seen what is being corrected. Undated, so the
  // chronological comparator would otherwise have put it first.
  const deduction = bucket === 'leistung' ? deductionRow(row) : null;
  if (deduction) {
    sumRaw += deduction.amount;
    items.push(deduction);
  }
  // The pooled per-hectare share, appended last for the same reason the deduction is: it
  // is not one of the cycle's own bookings but a share of a farm-wide pot, and it reads
  // where the reader has already seen the bookings it sits beside. Undated, so the
  // chronological comparator above would otherwise have put it first.
  const pooled = pooledHaRow(row, bucket);
  if (pooled) {
    sumRaw += pooled.amount;
    items.push(pooled);
  }
  // The Substrat charge, last, on the pooled share's argument: not a booking of the cycle's
  // but an imputed part of the line, so it reads after the bookings it sits beside.
  const substrat = substratRow(row, bucket);
  if (substrat) {
    sumRaw += substrat.amount;
    items.push(substrat);
  }
  const total = round2(sumRaw);
  // #2508 — a Σ-of-bookings line has no derivation: no factors ⇒ no Rechenweg table.
  return { kind: 'operations', factors: [], result: null, items, total, lineValue, reconciles: reconciles(total, lineValue) };
}

/**
 * HOF-518 — the Landkosten Rechenweg: ``Fläche x Pachtansatz je Hektar = €``.
 *
 * Two factors and no Einzelposten, because there are none to have: the charge is IMPUTED on
 * every hectare the cycle covers, including hectares the farm owns, so no invoice, no
 * operation and no booking stands behind it. That is exactly why the line has to open — a
 * cost a farmer cannot trace to a document is the one they most need told how it was made.
 *
 * The estimate badge follows the price GRADE (``land_estimate.estimate``), not the fact that
 * a rate was applied at all: a €/ha derived from the farm's own Pachtvertrag is not a guess,
 * even though it is still spread flat. ``basis`` says the second part; the badge must not
 * claim the first.
 */
function buildLandEstimateBreakdown(row: DeckungsbeitragRow): SourceBreakdown {
  // Non-null by the dispatch guard; keep a defensive fallback anyway.
  const est = row.land_estimate ?? {
    unit_price: 0,
    unit: '€/ha',
    area_ha: 0,
    basis: 'flat_per_hectare',
    source: '',
    estimate: true,
    catalog_item_id: '',
    observation_date: null,
  };
  const lineValue = row.direct_costs.land;
  // The engine's own figure, never re-derived here: ``area x rate`` computed in the browser
  // would drift a cent off a rounded persisted line and mint a false „Σ stimmt nicht".
  const total = round2(lineValue);
  const isEstimate = est.estimate;
  const factors: BreakdownFactor[] = [
    {
      key: 'area',
      label: 'Fläche',
      value: `${formatNumber(est.area_ha)} ha`,
      links: [farmLink(`/farm?lense=crop_cycles&item=${enc(row.crop_cycle_id)}`, cropLinkLabel(row))],
      estimated: false,
      note: LAND_EVERY_HECTARE_NOTE,
    },
    {
      key: 'unit_price',
      label: 'Pachtansatz je Hektar',
      // The rate's VALUE is its link, exactly as on Saatgut: the anchor text is the number
      // the Preisspiegel opens on, so the reader sees where the click lands.
      value: null,
      links: [
        {
          to: preisspiegelHref(est.catalog_item_id, est.observation_date),
          target: 'preisspiegel',
          label: `${formatEur(est.unit_price)}/ha`,
        },
      ],
      estimated: isEstimate,
      note: est.contract_rates_ambiguous
        ? LAND_AMBIGUOUS_CONTRACTS_NOTE
        : isEstimate
          ? LAND_ESTIMATED_RATE_NOTE
          : LAND_CONTRACT_RATE_NOTE,
    },
  ];
  return {
    kind: 'land_estimate',
    factors,
    result: {
      label: isEstimate ? 'Geschätzter Wert' : 'Landkosten',
      value: formatEur(total),
    },
    items: [],
    total,
    lineValue,
    reconciles: Math.abs(total - lineValue) < 0.005,
  };
}

function buildSaatgutEstimateBreakdown(row: DeckungsbeitragRow): SourceBreakdown {
  // Non-null by the dispatch guard; keep a defensive fallback anyway.
  const est = row.saatgut_estimate ?? { basis: 'pflanze', unit_price: 0, quantity: 0, source: '' };
  const lineValue = row.direct_costs.saatgut;
  const inputs = est.breakdown ?? [];
  // #2487 Part B — a PRODUCT-BACKED leg reconciles off the EXACT per-input Σ gross value (each
  // input's persisted ``value``), NOT the rounded weighted aggregate ``quantity × unit_price``
  // (which can drift a cent on an uneven multi-lot split → a false "Σ stimmt nicht"). The #2441
  // geometry estimate (no breakdown) keeps the Fläche × Dichte = Pflanzen × Preis form.
  const gross =
    inputs.length > 0 ? round2(inputs.reduce((sum, i) => sum + i.value, 0)) : round2(est.quantity * est.unit_price);
  // #2461 — a true-perennial establishment estimate is amortised straight-line over its
  // stand life, so the reconciling line total is the per-year share, not the gross stand.
  const years =
    est.cost_treatment === 'establishment' && est.amortise_lifetime_years && est.amortise_lifetime_years > 0
      ? est.amortise_lifetime_years
      : undefined;
  // #2593 — a stand OUTSIDE its window books nothing this year: the materialiser already put
  // 0,00 € on the line, and dividing the gross by the stand life here is exactly what made the
  // drawer disagree with it ("Σ stimmt nicht … 600,00 € vs. 0,00 €"). The stand block says so
  // explicitly, so the breakdown follows the verdict rather than re-deriving past it.
  const zeroStand = zeroStateStand(row);
  const total = zeroStand ? 0 : years ? round2(gross / years) : gross;
  const amortise = (value: number): number => (zeroStand ? 0 : years ? value / years : value);
  // #2508 — the Bestand anchor is labelled with the KULTUR it opens, not "im Betrieb".
  const ccLink = farmLink(`/farm?lense=crop_cycles&item=${enc(row.crop_cycle_id)}`, cropLinkLabel(row));
  const meta = SAATGUT_BASIS_META[est.basis] ?? SAATGUT_BASIS_FALLBACK;
  // #2487 Part B — one row per IDENTIFIED Pflanzgut input (variety product + qty), each carrying
  // its amortised € share so the per-input breakdown reaches the drawer; the last row absorbs the
  // rounding residual so Σ items == the reconciling total exactly. A #2441 geometry estimate (no
  // breakdown) keeps the single aggregate Bestand row.
  let items: BreakdownItem[];
  if (inputs.length > 0) {
    let allocated = 0;
    items = inputs.map((input, i) => {
      const amount = i === inputs.length - 1 ? round2(total - allocated) : round2(amortise(input.value));
      allocated = round2(allocated + amount);
      // #2538 — a lot priced off the crop-level price book also anchors its OWN Preisspiegel
      // item + day, so a multi-lot leg addresses each lot's price rather than one blended
      // aggregate. A lot priced from its variety product carries no keys → crop_cycle anchor only.
      const href = preisspiegelHref(input.catalog_item_id, input.observation_date);
      const links: BreakdownLink[] = [ccLink];
      if (href !== PREISSPIEGEL_APP) {
        links.push({ to: href, target: 'preisspiegel', label: `${formatEur(input.unit_price)}/${meta.unit}` });
      }
      return {
        key: input.product_id ?? `saatgut-input-${i}`,
        date: null,
        label: input.variety ?? '',
        detail: `${input.quantity} ${meta.detailNoun}`,
        amount,
        qty: null,
        unit: null,
        links,
        ...OWN,
      };
    });
  } else {
    // The single Bestand anchor: Pflanzen/Menge → crop_cycle geometry in /farm. Its Posten
    // identity IS the Kultur link (#2508), so the primary label stays empty.
    items = [
      {
        key: 'saatgut-bestand',
        date: null,
        label: '',
        detail: meta.detailNoun,
        amount: total,
        qty: null,
        unit: null,
        links: [ccLink],
        ...OWN,
      },
    ];
  }
  // #2508 — the #2441/#2461 formula prose becomes the generic Rechenweg: Fläche →
  // Bestand → Preis (→ Nutzungsdauer), closing on the € the line takes.
  const factors: BreakdownFactor[] = [];
  if (row.area_ha != null) {
    factors.push({
      key: 'area',
      label: 'Fläche',
      value: `${formatNumber(row.area_ha)} ha`,
      links: [],
      estimated: false,
      note: null,
    });
  }
  factors.push({
    key: 'bestand',
    label: meta.factor,
    value: `${formatNumber(est.quantity)} ${meta.qtyNoun}`,
    links: [ccLink],
    estimated: false,
    note: null,
  });
  factors.push({
    key: 'unit_price',
    label: `Preis je ${meta.unit}`,
    // #2508 — the price's VALUE is its link: the anchor text is the price the Preisspiegel
    // opens on, so the reader sees where the click lands instead of "im Preisspiegel".
    value: null,
    links: [
      {
        // #2538 — the exact catalog item + observation day this price was published under,
        // degrading to the app root whenever the fact cannot address one.
        to: preisspiegelHref(est.catalog_item_id, est.observation_date),
        target: 'preisspiegel',
        label: `${formatEur(est.unit_price)}/${meta.unit}`,
      },
    ],
    estimated: true,
    note: ESTIMATED_PRICE_NOTE,
  });
  // #2461/#2608 — the Nutzungsdauer/Kostenbehandlung factor. Exactly ONE of the two branches
  // renders, and each reads ONE authority: the establishment branch reads the estimate's own
  // ``amortise_lifetime_years`` (the RESOLVED span the € was divided by — never
  // ``stand.stand_life_years``, which the #2593 Anlagealter factor below reports separately),
  // and the annual branch reads the treatment TOKEN alone (an annual fact carries no
  // lifetime, so there is no number here to be wrong).
  //
  // Neither renders on a pre-#2608 fact that carries no treatment at all — the Rechenweg
  // keeps its plain Bestand × Preis shape rather than asserting a Nutzungsdauer nobody
  // stamped. That is every prod fact until the post-deploy re-materialise.
  if (years) {
    factors.push({
      key: 'lifetime',
      label: 'Nutzungsdauer (Etablierung)',
      value: `${formatNumber(years)} Jahre`,
      links: [],
      estimated: false,
      note: AMORTISE_NOTE,
    });
  } else if (est.cost_treatment === 'annual') {
    factors.push({
      key: 'lifetime',
      label: 'Nutzungsdauer',
      // "1 Jahr" is derived from the TOKEN, not from a fact field — the worker deliberately
      // emits no lifetime on the annual branch so nothing can divide by one.
      value: '1 Jahr',
      links: [],
      estimated: false,
      note: est.basis === 'pflanze' ? ANNUAL_NOTE_PFLANZE : ANNUAL_NOTE_SAATGUT,
    });
  }
  // #2593 — the stand verdict is a GENERIC factor (#2508), not a state-specific render
  // template: the render layer prints ``label``/``value``/``note`` and interprets nothing.
  // Emitted ONLY for a zero state — the states that book a real share are already told by the
  // Nutzungsdauer factor, and adding a row there would change the 100%-of-prod ``unknown``
  // render.
  if (zeroStand) {
    factors.push({
      key: 'stand',
      label: 'Anlagealter',
      value: standReasonText(zeroStand),
      links: [],
      // A year the operator assumed is exactly as "estimated" as a price from the price book;
      // only a farmer-stated Anlagejahr is a booked fact.
      estimated: zeroStand.provenance !== 'farmer_stated',
      note: standProvenanceNote(zeroStand.provenance),
    });
  } else if (unplacedEstablishmentStand(row)) {
    // HOF-466 — the one state left that the comment above deliberately excluded. It books a
    // real share, so it keeps every € and its ``Anteil dieses Jahres`` label; what it gains
    // is the row saying the window behind that share was never placed. ``else if``, not a
    // second ``if``: the two are mutually exclusive by state, and the branch structure is
    // what guarantees a line can never carry two ``key: 'stand'`` factors telling a reader
    // two different stories. Always ``estimated`` — the engine stamps ``unknown`` precisely
    // because it cannot justify the figure.
    factors.push({
      key: 'stand',
      label: 'Anlagealter',
      value: UNPLACED_STAND_TEXT,
      links: [],
      estimated: true,
      note: standProvenanceNote('unknown'),
    });
  }
  const result: BreakdownResult = {
    // #2593 — a written-off / not-yet-planted stand takes NO share this year, so the closing
    // row must stop reading "Anteil dieses Jahres: 600,00 €" over a 0,00 € line.
    label: zeroStand ? 'Kein Anteil dieses Jahres' : years ? 'Anteil dieses Jahres' : 'Geschätzter Wert',
    value: formatEur(total),
  };
  return {
    kind: 'saatgut_estimate',
    factors,
    result,
    items,
    total,
    lineValue,
    reconciles: reconciles(total, lineValue),
  };
}

/**
 * #2695 E — the Rechenweg behind a MODELLED Leistung: ``Erntemenge x Buchpreis``.
 *
 * The producer (#2695 D) puts every operand AND both their units on the fact for exactly this
 * reason, so this builder renders and does not calculate. Two rules follow from that, and both
 * are load-bearing:
 *
 * 1. **The € comes off the fact.** ``est.value`` is what the engine persisted and what the
 *    cascade line, DkfL and DB1 were all computed from. Re-multiplying ``quantity x
 *    unit_price`` here would mean re-implementing the dt<->t/kg conversion in TypeScript, and
 *    a UI that re-derives can disagree with the number underneath it (the "Σ stimmt nicht"
 *    class of defect #2593 had to remove from the Saatgut leg).
 * 2. **Both units are printed verbatim.** ``formatBookPrice``/``formatBookQty``, never
 *    ``formatEurPerUnit``/``formatQty`` — the latter collapse every unit that is not "dt" to
 *    "kg", which would print a €/t book price as "€/kg". Both operands' units vary
 *    independently (€/t vs €/dt vs €/kg against dt vs kg), so neither may be inferred from the
 *    other or from the crop.
 *
 * The closing row says what the € IS, because this is the one line in the cascade whose number
 * nobody has paid.
 */
function buildLeistungEstimateBreakdown(row: DeckungsbeitragRow): SourceBreakdown {
  // Non-null by the dispatch guard; keep a defensive fallback anyway.
  const est =
    row.leistung_estimate ??
    ({ value: 0, unit_price: 0, price_unit: '', quantity: 0, quantity_unit: '', source: '' } as LeistungEstimate);
  const lineValue = row.leistung;
  const total = est.value;
  const ccLink = farmLink(`/farm?lense=crop_cycles&item=${enc(row.crop_cycle_id)}`, cropLinkLabel(row));
  const factors: BreakdownFactor[] = [
    {
      key: 'quantity',
      label: 'Erntemenge',
      value: formatBookQty(est.quantity, est.quantity_unit),
      links: [ccLink],
      // NOT estimated: the harvest really was recorded. Only the price is modelled, and
      // badging both would blunt the one badge that carries the warning.
      estimated: false,
      note: HARVEST_QTY_NOTE,
    },
    {
      key: 'unit_price',
      label: 'Preis',
      // #2508 — the price's VALUE is its link, so the anchor text is the price the
      // Preisspiegel opens on rather than a category noun.
      value: null,
      links: [
        {
          to: preisspiegelHref(est.catalog_item_id, est.observation_date),
          target: 'preisspiegel',
          label: formatBookPrice(est.unit_price, est.price_unit),
        },
      ],
      estimated: true,
      // The book row's own justification, so the drawer can say WHY this € and not another.
      // Falls back to the generic note when the row names no anchor — never to null, because
      // this is the factor whose hover carries the "not booked revenue" claim.
      note: est.anchor ? `${ESTIMATED_LEISTUNG_PRICE_NOTE} ${est.anchor}` : ESTIMATED_LEISTUNG_PRICE_NOTE,
    },
  ];
  // The Rechenweg's result is the GROSS (Menge × Buchpreis); ``leistung`` is net of what
  // the buyer withheld. Since HOF-102's fix round removed the estimate's revenue fence,
  // a commissioned cycle can land HERE — so this branch needs the same deduction row the
  // Posten panel has, or its ✓ accuses correct data of not adding up.
  const estimateItems: BreakdownItem[] = [
    {
      key: 'leistung-estimate',
      date: est.observation_date ?? null,
      label: ESTIMATED_LEISTUNG_ITEM_LABEL,
      detail: est.source || null,
      amount: total,
      qty: null,
      unit: null,
      links: [ccLink],
      ...OWN,
    },
  ];
  const estimateDeduction = deductionRow(row);
  if (estimateDeduction) estimateItems.push(estimateDeduction);
  const estimateTotal = round2(estimateItems.reduce((sum, item) => sum + item.amount, 0));
  return {
    kind: 'leistung_estimate',
    factors,
    result: { label: ESTIMATED_LEISTUNG_RESULT_LABEL, value: formatEur(total) },
    // One Einzelposten so the table reconciles instead of totalling 0 against a real line.
    // ``qty``/``unit`` stay NULL deliberately: PostenCell would render them through
    // formatQty/formatEurPerUnit and relabel a t/€-t pair as kg. The identity rides ``label``.
    items: estimateItems,
    total: estimateTotal,
    lineValue,
    reconciles: reconciles(estimateTotal, lineValue),
  };
}

/**
 * The Gemeinkosten drill-down: the four D2 Absatz-pool lines, then whatever manual or
 * place-standing-cost shares the fact also carries.
 *
 * ALL FOUR pool lines render, always. An uncaptured pool reads "noch nicht erfasst" at
 * 0,00 € rather than vanishing, because "nobody has modelled this Hütte" and "this Hütte
 * cost nothing" are different statements and only one of them is a number. The Verpackung
 * and Kühlung lines in the same drawer already say exactly this.
 */
/**
 * The one extra row a per-hectare pooled share adds to its bucket's drill-down: unpinned
 * diesel on Maschinen, tunnel electricity on Sonstige Direktkosten.
 *
 * It is NOT a booking of this cycle's — it is this cycle's hectare share of a pot the whole
 * period shared — so it names the pot and the weight rather than a document, and it carries
 * the caveat the model attaches to it: §II.4's Feld-vs-Auslieferung split has not happened
 * (so delivery diesel is in the per-hectare charge), and packing-hall power is not tunnel
 * power. Saying that on the row is the difference between a number and a claim.
 */
function pooledHaRow(row: DeckungsbeitragRow, bucket: string): BreakdownItem | null {
  const pooled =
    bucket === 'maschinen' ? row.fuel_allocation : bucket === 'sonstige' ? row.energy_allocation : null;
  if (!pooled) return null;
  const isFuel = bucket === 'maschinen';
  const caveat = isFuel
    ? pooled.vehicle_split_pending
      ? ' · Feld/Auslieferung noch nicht getrennt'
      : ''
    : pooled.hall_power_unsplit
      ? ' · Halle/Tunnel noch nicht getrennt'
      : '';
  const scope = pooled.population === 'tunnel' ? 'Tunnelfläche' : 'Betriebsfläche';
  return {
    key: isFuel ? 'pooled-fuel' : 'pooled-energy',
    date: null,
    label: isFuel ? 'Betriebsstoffe (Umlage)' : 'Energie (Umlage, nur Tunnel)',
    detail: `${formatEur(pooled.pot_total)} ÷ ${scope}${caveat}`,
    amount: pooled.amount,
    qty: pooled.basis,
    unit: pooled.basis != null ? 'ha' : null,
    links: [],
    ...OWN,
  };
}

/**
 * The Substrat row a substrate culture adds to Sonstige Direktkosten: ``Laufmeter × l/lfm =
 * m³ × €/m³``, the HOF-518 Landkosten derivation one level down.
 *
 * It is NOT a booking — the worker IMPUTES it from the running metres and Ludger's rule and
 * adds it INTO ``direct_costs.sonstige`` — so it is a row of its own, appended after the dated
 * bookings like the pooled share, and its € is the engine's own arithmetic (``row_length ×
 * l/lfm ÷ 1000 × price``, unrounded, so the Σ reconciles to the cent; ``volume_m3`` is
 * rounded to 3 decimals and would drift). Without it the Σ comes up exactly one Substrat
 * short and the panel accuses a correct line of „Σ stimmt nicht".
 *
 * The badge follows ``estimate`` (either operand), and the identity IS the tile link: the
 * Preisspiegel ``substrat`` cell, on the observation day, exactly as the Pachtansatz links.
 */
function substratRow(row: DeckungsbeitragRow, bucket: string): BreakdownItem | null {
  const est = bucket === 'sonstige' ? row.substrat_estimate : null;
  if (!est) return null;
  const parts = [
    `${formatNumber(est.row_length_m)} lfm × ${formatNumber(est.litres_per_row_m)} l/lfm = ` +
      `${formatNumber(est.volume_m3)} m³ × ${formatBookPrice(est.unit_price, est.unit)}`,
  ];
  const hint = est.row_length_source ? SUBSTRAT_ROW_LENGTH_HINT[est.row_length_source] : undefined;
  if (hint) parts.push(hint);
  const elig = row.geometry_provenance?.substrate_eligibility;
  if (elig) {
    if (elig.basis === 'metres') {
      parts.push(
        `${formatNumber(est.row_length_m)} von ${formatNumber(est.total_row_length_m ?? elig.total)} lfm auf Substrat` +
          (elig.unknown > 0 ? ` (${elig.unknown} Tunnel ohne Angabe zum Anbausystem)` : ''),
      );
    } else {
      parts.push(
        `${elig.eligible} von ${elig.total} Tunneln auf Substrat` + (elig.unknown > 0 ? ` (${elig.unknown} ohne Angabe)` : ''),
      );
    }
  }
  if (est.rule_source) parts.push(`Richtwert: ${est.rule_source}`);
  const notes = [
    ...(est.price_estimate ? [SUBSTRAT_PRICE_ESTIMATE_NOTE] : []),
    ...(est.rule_estimated ? [SUBSTRAT_RULE_ESTIMATE_NOTE] : []),
    ...((est.grow_system_unknown ?? 0) > 0 ? [SUBSTRAT_UNKNOWN_GROW_SYSTEM_NOTE] : []),
  ];
  return {
    key: 'substrat-estimate',
    date: null,
    // The identity is the link when there is a tile to open; a block naming none keeps the
    // word as plain text rather than borrowing the app root as its provenance.
    label: est.catalog_item_id ? '' : 'Substrat',
    detail: parts.join(' · '),
    amount: (est.row_length_m * est.litres_per_row_m * est.unit_price) / 1000,
    qty: null,
    unit: null,
    links: est.catalog_item_id
      ? [{ to: preisspiegelHref(est.catalog_item_id, est.observation_date), target: 'preisspiegel', label: 'Substrat' }]
      : [],
    ...OWN,
    estimated: est.estimate,
    note: est.estimate ? (notes.length > 0 ? notes.join(' ') : SUBSTRAT_ESTIMATE_FALLBACK_NOTE) : null,
  };
}

// ── Layer C „Investition" (2026-10-06) ─────────────────────────────────────────
/** The cascade key of the Investition line (== ``layers.INVESTITION_LINE``; layers imports this module). */
const INVESTITION_KEY = 'investition';

const INVESTITION_COMPONENT_LABEL: Record<keyof InvestitionComponents, string> = {
  afa: 'AfA',
  zinsen: 'Zinsen',
  reparatur: 'Reparatur',
  versicherung: 'Versicherung',
  reparatur_versicherung: 'Reparatur+Versicherung',
  nutzung: 'Nutzungsentgelt',
};

const INVESTITION_ASSET_TEMPLATE_NOTE = 'Einzelne Werte dieses Objekts sind Standardannahmen, keine Betriebszahlen.';
/** An asset whose only gap is its build year: no operand is a default, it simply counts in every year (M8). */
const INVESTITION_ASSET_NO_YEAR_NOTE = 'Baujahr fehlt — gilt in jedem Jahr';
/** The operands the engine can default, in words a farmer reads. */
const INVESTITION_DEFAULTED_LABEL: Record<string, string> = {
  interest_rate: 'Zinssatz',
  repair_rate: 'Reparatursatz',
  insurance_rate: 'Versicherungssatz',
  useful_life_years: 'Nutzungsdauer',
};

/** What a class's share is counted in — the quantity's unit, and the unit a €-rate is per. */
const INVESTITION_KEY_UNIT: Record<InvestitionKey, { qty: string; per: string; unknown: string }> = {
  ha: { qty: 'ha', per: 'ha', unknown: 'Fläche unbekannt' },
  lfm: { qty: 'lfm', per: 'lfm', unknown: 'Laufmeter unbekannt' },
  pflanzen: { qty: 'Pflanzen', per: 'Pflanze', unknown: 'Pflanzenzahl unbekannt' },
};

const INVESTITION_NO_POPULATION = 'keine Kultur trägt diese Klasse';

/**
 * The fact's ``cultivation_system`` tokens a farmer can read. An unknown token is dropped,
 * never printed raw — the same rule as an unknown labour Posten kind.
 */
const CULTIVATION_SYSTEM_LABEL: Record<string, string> = { tunnel: 'Tunnel', tray: 'Stellage', open_field: 'Freiland' };

const INVESTITION_CALCULATED_NOTE =
  'Kalkulatorischer Richtwert — Investition, Nutzungsdauer und Sätze sind vom Betrieb noch nicht bestätigt.';
const INVESTITION_BOOKED_NOTE =
  'Gebuchter Betrag des ganzen Betriebs, anteilig auf die Anbauten verteilt, die diese Klasse tragen — keine Buchung auf diesen Anbau.';

/** Hectares read to the gram of the cycle (0,444 ha); every other count to two decimals. */
const HA3 = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 });
const formatKeyQty = (value: number, key: InvestitionKey): string =>
  `${key === 'ha' ? HA3.format(value) : formatNumber(value)} ${INVESTITION_KEY_UNIT[key].qty}`;
const formatRate = (rate: number): string => `${formatNumber(rate * 100)} %`;

/** ``AfA 12.432,00 € + Zinsen 1.243,20 €`` — the components in their declared order. */
const investitionComponentsText = (c: InvestitionComponents): string =>
  (Object.keys(INVESTITION_COMPONENT_LABEL) as (keyof InvestitionComponents)[])
    .filter((k) => c[k] !== undefined)
    .map((k) => `${INVESTITION_COMPONENT_LABEL[k]} ${formatEur(c[k]!)}`)
    .join(' + ');

/**
 * Ludger's formula, € per key unit and year, times the cycle's key: „0,444 ha × 37.800
 * €/ha/Jahr (AfA 280.000 € ÷ 10 J = 28.000 · Zinsansatz 2 % auf ½ Kapital = 2.800 ·
 * Reparatur+Versicherung 2,5 % = 7.000)". Each operand prints only where the fact carries it;
 * without the per-unit rates the row degrades to its components rather than to a guess.
 */
function calculatedRechenweg(cls: InvestitionClass): string {
  const pu = cls.per_unit;
  if (!pu) return investitionComponentsText(cls.components);
  const unit = INVESTITION_KEY_UNIT[cls.key];
  const afa =
    cls.capex_per_unit !== undefined && cls.useful_life_years !== undefined
      ? `AfA ${formatNumber(cls.capex_per_unit)} € ÷ ${formatNumber(cls.useful_life_years)} J = ${formatNumber(pu.afa)}`
      : `AfA ${formatNumber(pu.afa)}`;
  const zinsen =
    cls.interest_rate !== undefined
      ? `Zinsansatz ${formatRate(cls.interest_rate)} auf ½ Kapital = ${formatNumber(pu.zinsen)}`
      : `Zinsansatz ${formatNumber(pu.zinsen)}`;
  const rv =
    cls.repair_insurance_rate !== undefined
      ? `Reparatur+Versicherung ${formatRate(cls.repair_insurance_rate)} = ${formatNumber(pu.reparatur_versicherung)}`
      : `Reparatur+Versicherung ${formatNumber(pu.reparatur_versicherung)}`;
  const rate = `${formatNumber(pu.afa + pu.zinsen + pu.reparatur_versicherung)} €/${unit.per}/Jahr (${afa} · ${zinsen} · ${rv})`;
  return cls.key_value !== null ? `${formatKeyQty(cls.key_value, cls.key)} × ${rate}` : rate;
}

/**
 * A farm pool split over the cycles that carry the class: „Reparatur 179.915,08 € × 0,444 ha
 * ÷ 75,9 ha = 1.052,47 €". The „=" is the ENGINE's figure for this cycle, never re-derived.
 */
function bookedRechenweg(cls: InvestitionClass): string {
  const poolKeys = cls.pool ? Object.keys(cls.pool).length : 0;
  const pool =
    cls.pool && poolKeys === 1
      ? investitionComponentsText(cls.pool)
      : cls.pool && poolKeys > 1
        ? `(${investitionComponentsText(cls.pool)})`
        : cls.pool_total !== undefined
          ? `Betriebssumme ${formatEur(cls.pool_total)}`
          : null;
  if (pool === null) return investitionComponentsText(cls.components);
  if (cls.key_value !== null && cls.population_key_total !== undefined && cls.population_key_total > 0) {
    return `${pool} × ${formatKeyQty(cls.key_value, cls.key)} ÷ ${formatKeyQty(cls.population_key_total, cls.key)} = ${formatEur(cls.total)}`;
  }
  if (cls.share !== undefined) return `${pool} × Anteil ${formatRate(cls.share)} = ${formatEur(cls.total)}`;
  return `${pool} → ${formatEur(cls.total)}`;
}

/**
 * ``0,0175`` → „1,75 %", ``0,02`` → „2 %" — a fraction as a percentage, de-DE, with up to ``max``
 * decimals and no trailing zeros by default, so a rate prints exactly as recorded (M7).
 */
const pct = (x: number, max = 2, min = 0): string =>
  `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: min, maximumFractionDigits: max }).format(x * 100)} %`;

/** `` = 7.500,00 €`` for a component the row carries, else nothing. */
const componentEur = (components: InvestitionComponents, key: keyof InvestitionComponents): string => {
  const value = components[key];
  return value !== undefined && value !== null ? ` = ${formatEur(value)}` : '';
};

/**
 * A registered tunnel's own Rechenweg. Owned: Anschaffung, AfA over the useful life, Zinsen on
 * the half capital, Reparatur (+ foil cycle), Versicherung and the cycle's share. A use charge
 * is the booked Nutzungsentgelt × the share. Every operand prints only where the row carries it
 * (a use-charge row omits the owned-only keys; a refused row omits the share).
 */
function assetRechenweg(cls: InvestitionClass): string {
  const share =
    cls.share !== undefined
      ? `Beträge nach Anteil ${pct(cls.share, 1, 1)}` +
        (cls.key_value !== null && cls.population_key_total !== undefined
          ? ` (${HA3.format(cls.key_value)} von ${HA3.format(cls.population_key_total)} ${INVESTITION_KEY_UNIT[cls.key].qty})`
          : '')
      : null;
  if (cls.ownership === 'use_charge') {
    const charge = cls.use_charge_eur_per_year !== undefined ? `Nutzungsentgelt ${formatEur(cls.use_charge_eur_per_year)}/Jahr` : investitionComponentsText(cls.components);
    return [charge, share].filter((x): x is string => !!x).join(' · ');
  }
  const parts: string[] = [];
  if (cls.paid_off) parts.push('abgeschrieben — nur Reparatur und Versicherung');
  if (cls.acquisition_cost_eur !== undefined) parts.push(`Anschaffung ${formatEur(cls.acquisition_cost_eur)}`);
  // Each component's € is THIS cycle's (after the share, which the share part names).
  const c = cls.components;
  if (cls.useful_life_years !== undefined) parts.push(`AfA über ${formatNumber(cls.useful_life_years)} Jahre${componentEur(c, 'afa')}`);
  if (cls.interest_rate !== undefined) parts.push(`Zinsen ${pct(cls.interest_rate)} auf das halbe Kapital${componentEur(c, 'zinsen')}`);
  if (cls.repair_rate !== undefined) {
    const foil =
      cls.foil_cost_eur != null && cls.foil_cycle_years != null
        ? ` + Folie ${formatEur(cls.foil_cost_eur)} / ${formatNumber(cls.foil_cycle_years)} Jahre`
        : '';
    parts.push(`Reparatur ${pct(cls.repair_rate)}${foil}${componentEur(c, 'reparatur')}`);
  }
  if (cls.insurance_rate !== undefined) parts.push(`Versicherung ${pct(cls.insurance_rate)}${componentEur(c, 'versicherung')}`);
  if (share) parts.push(share);
  if (cls.defaulted && cls.defaulted.length > 0) {
    parts.push(`Standard: ${cls.defaulted.map((k) => INVESTITION_DEFAULTED_LABEL[k] ?? k).join(', ')}`);
  }
  if (cls.capex_from_template) parts.push('Anschaffungswert aus dem Richtwert, keine Betriebszahl');
  return parts.length > 0 ? parts.join(' · ') : investitionComponentsText(cls.components);
}

/** One class as a Beleg: its kind, its name, its Rechenweg (or refusal) and its €. */
function investitionItem(cls: InvestitionClass): BreakdownItem {
  const refused = cls.refused !== undefined;
  const parts: string[] = [];
  if (cls.refused === 'no_key') parts.push(INVESTITION_KEY_UNIT[cls.key].unknown);
  else if (cls.refused === 'no_population') parts.push(INVESTITION_NO_POPULATION);
  else {
    if (cls.asset) parts.push(assetRechenweg(cls));
    else parts.push(cls.method === 'calculated' ? calculatedRechenweg(cls) : bookedRechenweg(cls));
    const scope = [
      ...(cls.applies_to?.crops ?? []),
      ...(cls.applies_to?.cultivation_systems ?? []).map((s) => CULTIVATION_SYSTEM_LABEL[s]).filter((s): s is string => !!s),
    ];
    if (cls.method === 'booked' && scope.length > 0) parts.push(`nur auf: ${scope.join(', ')}`);
    if (cls.source && !cls.asset) parts.push(`${cls.method === 'calculated' ? 'Richtwert' : 'Quelle'}: ${cls.source}`);
  }
  const estimated = !refused && cls.estimated;
  return {
    key: `investition:${cls.id}`,
    // Spec §3.7: ownership, not method, labels an asset row.
    kind: cls.asset ? (cls.ownership === 'use_charge' ? 'Nutzungsentgelt' : 'eigen') : cls.method === 'calculated' ? 'kalkulatorisch' : 'gebucht',
    date: null,
    label: cls.label,
    detail: parts.join(' · '),
    amount: refused ? 0 : cls.total,
    qty: null,
    unit: null,
    links: [],
    ...OWN,
    estimated,
    note: !estimated
      ? null
      : cls.asset
        ? (cls.defaulted ?? []).length === 0 && !cls.capex_from_template
          ? INVESTITION_ASSET_NO_YEAR_NOTE
          : INVESTITION_ASSET_TEMPLATE_NOTE
        : cls.method === 'calculated'
          ? INVESTITION_CALCULATED_NOTE
          : INVESTITION_BOOKED_NOTE,
  };
}

/**
 * The Investition line's Belege: one row per class, Σ against ``direct_costs.investition``.
 * The farm pools charged to NO cycle (``investition.unallocated``) are deliberately not here —
 * they are not in this DB, and a row in this table is read as part of its Σ. The drawer lists
 * them apart, under the Rechenweg.
 */
function buildInvestitionBreakdown(row: DeckungsbeitragRow): SourceBreakdown {
  const lineValue = row.direct_costs.investition;
  const items = (row.investition?.classes ?? []).map(investitionItem);
  const total = round2(items.reduce((sum, i) => sum + i.amount, 0));
  return { kind: 'investition', factors: [], result: null, items, total, lineValue, reconciles: reconciles(total, lineValue) };
}

function buildAllocationsBreakdown(row: DeckungsbeitragRow): SourceBreakdown {
  const lineValue = row.allocated_overhead;
  let sumRaw = 0;
  const pools: BreakdownItem[] = (row.d2_pools.length > 0 ? row.d2_pools : []).map((p) => {
    sumRaw += p.amount;
    return {
      key: `d2-${p.slot}`,
      date: null,
      label: p.label,
      // What the farmer needs to see is the derivation: this cycle's kg out of a pool of
      // N €. An uncaptured pool has no derivation to show and says so instead.
      detail: p.captured
        ? `${formatEur(p.pool_total)} ÷ kg durch den Pool`
        : 'noch nicht erfasst',
      amount: p.amount,
      qty: p.basis,
      unit: p.basis != null ? 'kg' : null,
      links: [],
      ...OWN,
    };
  });
  const items: BreakdownItem[] = row.allocations
    .filter((a) => a.source !== 'd2')
    .map((a, i) => {
    sumRaw += a.amount;
    const method = a.method ? a.method.replace(/_/g, ' ') : null;
    return {
      key: a.cost_center_id ?? a.target_crop_cycle_id ?? `alloc-${i}`,
      date: null,
      label: a.cost_center_id ? `Kostenstelle ${a.cost_center_id}` : 'Gemeinkosten-Anteil',
      detail: method,
      amount: a.amount,
      qty: null,
      unit: null,
      // cost_center has no lens today → link the target crop_cycle in /farm as the anchor,
      // labelled with the Kultur it opens (#2508, was the category-named "im Betrieb").
      links: a.target_crop_cycle_id
        ? [farmLink(`/farm?lense=crop_cycles&item=${enc(a.target_crop_cycle_id)}`, cropLinkLabel(row))]
        : [],
      // The #2442 Gemeinkosten audit array is already a per-allocation list, so each row IS
      // its own figure here — the #2769-a discriminator is about a share DEDUCTED from
      // another row in the same table, which this is not.
      ...OWN,
    };
  });
  const total = round2(sumRaw);
  return {
    kind: 'allocations',
    factors: [],
    result: null,
    items: [...pools, ...items],
    total,
    lineValue,
    reconciles: reconciles(total, lineValue),
  };
}

// ── #4172 wave 2: the labour lines' Rechenweg ───────────────────────────────────

/** German name per ``LabourPostenKind`` — the Posten row's primary descriptor. */
const LABOUR_KIND_LABEL: Record<LabourPostenKind, string> = {
  picking: 'Pflücklohn',
  insurance: 'Erntehelferversicherung',
  surcharge: 'Arbeitgeberzuschlag',
  packing_labour: 'Packhalle',
  // A material row prints under its ARTICLE's own name (``article_label``: „Schale 500 g",
  // „IFCO-Steige"); these are what it falls back to on a fact that carries none.
  packaging_tray: 'Schalen',
  packaging_crate: 'Kisten',
  packaging_pool_fee: 'Mehrwegsteigen',
  cooling: 'Kühlung, Pfandkisten',
  planting: 'Pflanzarbeit',
  field_care: 'Pflegearbeit',
  // HOF-692 — the ``overhead`` class booked ON this Anbau (Tunnelbau, Reparaturen, Fahren,
  // Teamleitung, Ordnung machen …): this Anbau's own work, just neither care nor planting.
  // Work booked on no field is HOF-644's ``pool_share``, „Umlage Arbeit ohne Feld".
  overhead: 'Sonstige Arbeit',
  // HOF-644
  employer_uplift: 'Lohnnebenkosten (Arbeitgeber)',
  // HOF-705
  employer_hour_cost: 'Arbeitgeberkosten laut Lohnabrechnung',
  pool_share: 'Umlage Arbeit ohne Feld',
  fixed_share: 'Fester Kulturanteil',
};

/** The bucket's own line on the drawer — used in the Rechenweg's result row. */
const LABOUR_RESULT_LABEL: Record<string, string> = {
  pflueck: 'Pflücklohn',
  nebenkosten: 'Pflück-Nebenkosten',
  verpackung: 'Verpackung',
  kuehlung: 'Kühlung, Pfandkisten',
  pflanzung: 'Pflanzarbeit',
};

const PACKING_SHARE_NOTE =
  'In der Packhalle wird nicht je Kultur gestempelt. Die Lohnsumme des Monats wird deshalb ' +
  'nach den gepflückten Kilogramm verteilt — es ist ein Anteil, keine Buchung auf diesen Anbau.';

// HOF-644 — the three payroll-side kinds, each saying where its euro comes from.
const UPLIFT_NOTE =
  'Was der Betrieb über den Bruttolohn hinaus zahlt (Sozialversicherung, Umlagen, Pauschalsteuern) — ' +
  'der Durchschnitt der Personengruppe im Monat laut Lohnabrechnung, aufgeschlagen auf den Lohn darüber.';

// HOF-705 — decision B1 with the bookkeeper and the owner (2026-10-19).
const HOUR_COST_NOTE =
  'Was die Arbeit den Betrieb laut Lohnabrechnung kostet: die Stunden × der Durchschnitt je Stunde ' +
  'der Personengruppe im Monat (Lohn, Prämien, Sozialversicherung, Umlagen, Pauschalsteuern), ' +
  'abzüglich des schon gebuchten Lohns darüber. Prämien stecken im Durchschnitt; wo mehr Prämie ' +
  'gebucht ist als der Durchschnitt enthält, kann der Posten weniger als null sein. Ist die ' +
  'Personengruppe nicht bekannt, gilt der Durchschnitt des ganzen Betriebs im Monat.';

const POOL_SHARE_NOTE =
  'Arbeit ohne Feld (z.B. Fahren, Teamleitung): die Lohnsumme des Monats wird nach den gepflückten ' +
  'Kilogramm verteilt — es ist ein Anteil, keine Buchung auf diesen Anbau.';

const FIXED_SHARE_NOTE =
  'Festangestellte ohne Zeiterfassung: ihr fester Kulturanteil an den Arbeitgeberkosten, nach ' +
  'Kilogramm auf die Anbauten der Kultur verteilt — ein Anteil, keine Buchung auf diesen Anbau.';

const KUEHLUNG_TEMPLATE_NOTE =
  'Referenzsatz aus der Beratungsliteratur — der Betrieb hat noch keinen eigenen Kühlsatz bestätigt.';

const KUEHLUNG_CONFIRMED_NOTE = 'Vom Betrieb bestätigter Satz, kein Schätzwert.';

/** ``1.250 kg`` / ``48 Std.`` / ``6 Personen`` — whichever the item actually carries. */
/** € per piece: a tray costs a few cents, so the price needs the decimals a euro never does. */
const formatPiecePrice = (price: number): string =>
  `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(price)} €`;

const isMaterialKind = (kind: LabourPostenKind): boolean => kind.startsWith('packaging_');

/**
 * The hover copy behind a packaging-material row. Three things a farmer has to be able to
 * check, and each is stated with the row's own numbers: the pieces are a SHARE (of what the
 * crop delivered that month, by this cycle's picked kilos), the price is the farm's own
 * invoices, and a pool crate costs its operator's fee — never the deposit, which the buyer
 * pays back.
 */
function materialNote(item: LabourPostenItem): string {
  const share =
    item.share != null
      ? `Gezählt aus den Lieferscheinen der Kultur in diesem Monat; dieser Anbau trägt ${formatPercent(item.share)} der im Monat gepflückten Kilos und damit denselben Anteil der Stückzahl.`
      : 'Gezählt aus den Lieferscheinen der Kultur in diesem Monat und nach gepflückten Kilos verteilt.';
  const fee =
    item.kind === 'packaging_pool_fee'
      ? ' Angesetzt ist die Gebühr des Poolbetreibers je Steige, nicht das Pfand — das Pfand zahlt der Abnehmer zurück. Zurückgegebenes Leergut ist abgezogen.'
      : '';
  if (item.unit_price == null) {
    return `${share}${fee} Für diesen Artikel liegt keine Rechnung mit Stückpreis vor, deshalb steht er mit 0,00 € da.`;
  }
  const year = item.price_year != null ? ` ${item.price_year}` : '';
  const price = item.price_carried
    ? ` Der Stückpreis stammt aus${year} — es liegt keine Rechnung dieser Saison vor.`
    : ` Der Stückpreis ist der Mittelwert deiner Eingangsrechnungen${year}.`;
  return `${share}${fee}${price}`;
}

function labourItemDetail(item: LabourPostenItem): string | null {
  if (item.kind === 'employer_hour_cost') {
    // The hours the per-hour rate was charged on, and the booked wage it is netted against.
    // A pooled share carries no hours of its own, only the wage it adjusts.
    if (item.hours != null && item.hours > 0) {
      const wage = item.basis != null ? `, abzüglich ${formatEur(item.basis)} Lohn` : '';
      return `${formatNumber(item.hours)} Std. × Ø je Stunde der Personengruppe${wage}`;
    }
    return item.basis != null && item.basis > 0 ? `Ausgleich auf ${formatEur(item.basis)} Lohn` : null;
  }
  if (item.kind === 'employer_uplift') {
    // The basis of an uplift row is the WAGE it was charged on, in euro — not kilogrammes.
    return item.basis != null && item.basis > 0 ? `auf ${formatEur(item.basis)} Lohn` : null;
  }
  if (isMaterialKind(item.kind)) {
    if (item.pieces == null) return null;
    const pieces = `${formatNumber(item.pieces)} Stk.`;
    return item.unit_price != null ? `${pieces} × ${formatPiecePrice(item.unit_price)}` : `${pieces} · kein Rechnungspreis`;
  }
  const parts: string[] = [];
  if (item.hours != null && item.hours > 0) parts.push(`${formatNumber(item.hours)} Std.`);
  // HOF-692 — kilograms only on a SHARE row, where they are this Anbau's picked kilos and
  // so the basis of the „x % von y €" beside them. A wage row is people's time: hours and a
  // head count. The time records carry ``picked_kg`` too, and on a Pflegearbeit row it read
  // as harvest inside a line that is, by definition, every hour that is not a harvest hour.
  if (item.share != null && item.kg != null && item.kg > 0) parts.push(`${formatNumber(item.kg)} kg`);
  // A COUNT, never a name: this app never identifies an individual worker (DPIA §7b).
  if (item.worker_count != null && item.worker_count > 0) {
    parts.push(`${formatNumber(item.worker_count)} ${item.worker_count === 1 ? 'Person' : 'Personen'}`);
  }
  if (item.share != null && item.pool != null) {
    parts.push(`${formatSharePercent(shareOf(item))} von ${formatEur(item.pool)}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * A share row's exact share: its own amount over its pool. The engine stores ``share`` to two
 * decimals (0,04), which printed 4,42 % as „4,0 %" (2026-11-03); the euros were always right.
 */
export function shareOf(item: LabourPostenItem): number {
  return item.pool != null && item.pool > 0 ? Math.abs(item.amount) / item.pool : (item.share ?? 0);
}

/** ``4,42 %`` / ``12,5 %`` — enough decimals that pool × share visibly gives the amount. */
export const formatSharePercent = (share: number): string =>
  `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 2 }).format(share * 100)} %`;

const formatPercent = (share: number): string =>
  `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(share * 100)} %`;

/**
 * The Verpackung Rechenweg — pool × Anteil — emitted ONLY for a single-month line.
 *
 * A factor table states one derivation. Across several months there are several pools and
 * several shares, and collapsing them into one row would have to invent an aggregate „pool"
 * and an aggregate „share" that no month has: the Σ of the pools is not a pot anything was
 * taken out of, and the ratio of the sums is not any month's allocation key. The per-month
 * rows carry the real thing (each states its own share of its own pool), so the honest move
 * is to leave the table off rather than print a plausible number nobody computed.
 */
function packingFactors(row: DeckungsbeitragRow, items: LabourPostenItem[]): BreakdownFactor[] {
  if (items.length !== 1) return [];
  const only = items[0];
  if (only.pool == null || only.share == null || only.basis == null) return [];
  return [
    {
      key: 'pool',
      label: 'Lohnsumme der Packhalle',
      value: formatEur(only.pool),
      links: [],
      estimated: false,
      note: PACKING_SHARE_NOTE,
    },
    {
      key: 'basis',
      label: 'Gepflückte Kilogramm dieses Anbaus',
      value: `${formatNumber(only.basis)} kg`,
      links: [farmLink(`/farm?lense=crop_cycles&item=${enc(row.crop_cycle_id)}`, cropLinkLabel(row))],
      estimated: false,
      note: null,
    },
    {
      key: 'share',
      label: 'Anteil an der Pflückmenge des Monats',
      value: formatPercent(only.share),
      links: [],
      estimated: false,
      note: null,
    },
  ];
}

/** The Kühlung Rechenweg — Menge × Satz — off the fact's own ``kuehlung_estimate``. */
function coolingFactors(row: DeckungsbeitragRow): BreakdownFactor[] {
  const est = row.kuehlung_estimate;
  if (!est) return [];
  const estimated = row.kuehlung_estimated;
  return [
    {
      key: 'quantity',
      label: 'Gepflückte Menge',
      value: `${formatNumber(est.quantity)} kg`,
      links: [farmLink(`/farm?lense=crop_cycles&item=${enc(row.crop_cycle_id)}`, cropLinkLabel(row))],
      estimated: false,
      note: null,
    },
    {
      key: 'rate',
      label: 'Satz je Kilogramm',
      value: `${formatEur(est.rate)}/kg`,
      links: [],
      // The badge follows the price GRADE, not the fact that a rate was applied — the
      // HOF-518 Landkosten argument. A farm-confirmed rate is not a guess.
      estimated,
      note: estimated ? KUEHLUNG_TEMPLATE_NOTE : KUEHLUNG_CONFIRMED_NOTE,
    },
    ...(est.source
      ? [
          {
            key: 'source',
            label: 'Quelle des Satzes',
            value: est.source,
            links: [],
            estimated: false,
            note: null,
          },
        ]
      : []),
  ];
}

/**
 * #4172 wave 2 — one labour line's months.
 *
 * ``lineValue`` is the persisted bucket and ``total`` the Σ of the fact's own Posten for it,
 * so the reconciliation tick asks a REAL question: the engine writes both halves in the same
 * pass, and a mismatch means a bucket picked up money no Posten accounts for. That is worth
 * showing a farmer, and it is why this does not simply print the line value back.
 */
/**
 * The fact's Posten for one labour bucket, as breakdown rows, plus their raw Σ.
 *
 * Extracted so the OPERATIONS builder can reuse it for ``loehne`` — the one bucket both
 * sources feed. Identical output to the inline version it replaces.
 */
function labourPostenItems(row: DeckungsbeitragRow, bucket: string): { items: BreakdownItem[]; sumRaw: number } {
  // Every bucket the line spans (2026-10-07: Löhne carries Pflanzarbeit, Pflücklohn the
  // Nebenkosten), each item keyed by its OWN bucket so two members never collide.
  const posten = lineBuckets(bucket).flatMap((member) =>
    (row.labour_posten?.[member as keyof typeof row.labour_posten] ?? []).map((item, i) => ({ item, member, i })),
  );
  let sumRaw = 0;
  const items: BreakdownItem[] = posten.map(({ item, member, i }) => {
    // HOF-705 — B1's offset is the one signed Posten: more bonus booked than the average holds
    // makes it negative, and a magnitude would overstate the line (codex round 1).
    const amount = item.kind === 'employer_hour_cost' ? item.amount : Math.abs(item.amount);
    sumRaw += amount;
    return {
      key: `labour-${member}-${i}`,
      date: null,
      label: (isMaterialKind(item.kind) ? item.article_label : null) ?? LABOUR_KIND_LABEL[item.kind] ?? item.kind,
      detail: labourItemDetail(item),
      amount,
      qty: null,
      unit: null,
      links: [],
      ...OWN,
      // A packing share is a SHARE of a farm-wide monthly pot, not a booking on this cycle —
      // the #2769-a vocabulary, and the reason it exists. Same for cooling, whose € is a
      // reference rate applied to the harvest rather than money anybody was paid.
      ...(item.kind === 'packing_labour'
        ? { origin: 'apportioned' as const, estimated: true, note: PACKING_SHARE_NOTE }
        : {}),
      // HOF-644: a pool share and a fixed crop share are shares of a farm-wide monthly sum,
      // exactly as the packing hall is; the uplift is measured payroll, applied as a rate.
      ...(item.kind === 'pool_share' ? { origin: 'apportioned' as const, estimated: true, note: POOL_SHARE_NOTE } : {}),
      ...(item.kind === 'fixed_share'
        ? { origin: 'apportioned' as const, estimated: true, note: FIXED_SHARE_NOTE }
        : {}),
      ...(item.kind === 'employer_uplift' ? { note: UPLIFT_NOTE } : {}),
      ...(item.kind === 'employer_hour_cost' ? { note: HOUR_COST_NOTE } : {}),
      // Material is a share too — of the PIECES the crop delivered, not of a pot of money —
      // so it wears the same mark, with its own reason.
      ...(isMaterialKind(item.kind)
        ? { origin: 'apportioned' as const, estimated: true, note: materialNote(item) }
        : {}),
      ...(item.kind === 'cooling' && row.kuehlung_estimated
        ? { estimated: true, note: KUEHLUNG_TEMPLATE_NOTE }
        : {}),
      // The panel prints the month for a folded row; a period-level item (the surcharge,
      // the cooling charge) carries none and prints a dash, which is what it is.
      month: item.month,
    };
  });
  return { items, sumRaw };
}

function buildLabourBreakdown(row: DeckungsbeitragRow, bucket: string): SourceBreakdown {
  const lineValue = lineMagnitude(row, bucket);
  const posten = labourPosten(row, bucket);
  const { items, sumRaw } = labourPostenItems(row, bucket);
  const factors =
    bucket === 'verpackung' ? packingFactors(row, posten) : bucket === 'kuehlung' ? coolingFactors(row) : [];
  // A factor-derived line with no Posten behind it takes the ENGINE's own figure, exactly as
  // the Landkosten estimate does: re-deriving ``Menge × Satz`` in the browser would drift a
  // cent off the rounded persisted line and mint a false „Σ stimmt nicht" against a Rechenweg
  // that is the whole derivation. With Posten present the Σ is a real check and stays one.
  const total = posten.length === 0 && factors.length > 0 ? round2(lineValue) : round2(sumRaw);
  return {
    kind: 'labour_monthly',
    factors,
    result:
      factors.length > 0
        ? { label: LABOUR_RESULT_LABEL[bucket] ?? 'Summe', value: formatEur(lineValue) }
        : null,
    items,
    total,
    lineValue,
    reconciles: reconciles(total, lineValue),
  };
}

// ── public API ──────────────────────────────────────────────────────────────────
/** Whether the fact counted packaging pieces — priced or not. */
export function hasCountedMaterial(row: DeckungsbeitragRow): boolean {
  const m = row.verpackung_material;
  return m !== null && m !== undefined && (m.prices.length > 0 || m.unpriced.length > 0);
}

/**
 * Whether a cascade line is a selectable source line, a disabled (source-less) line,
 * or an inert subtotal/result:
 *   - subtotals/results/etablierung/DZ and any 0-value line → ``static`` (not a button), with
 *     ONE #2593 carve-out: a 0,00 € Saatgut line whose stand explains the zero.
 *   - Gemeinkosten → ``selectable`` iff the ``allocations`` audit array is present, else ``disabled``.
 *   - Saatgut with a #2441 estimate → ``selectable`` (formula, source-independent).
 *   - other source buckets → ``selectable`` on an ``operations`` row, ``disabled`` on a
 *     ``category_fallback`` row (no per-operation € to serve — since #2574 such a row may well
 *     serve RECORDS, it is the money that the category sum did not come from).
 *
 * #2574 deliberately does NOT touch this. A fallback row may well serve records (that is the
 * whole point of the zero-reason re-gate), but its € came from CATEGORY SUMS: opening
 * ``buildOperationsBreakdown`` there would Σ money-less operations to 0 against a non-zero
 * lineValue and mint a fresh "Σ stimmt nicht". Those lines stay ``disabled``; only the
 * disabled tooltip's wording was corrected, since "keine Einzelbuchungen" is now false.
 */
export function lineSelectability(
  row: DeckungsbeitragRow,
  key: string,
  /**
   * The cycle's served bookings. Optional, and the default is the pre-HOF-467-follow-up
   * behaviour: without them the „Preis fehlt" carve-out below cannot fire, because whether a
   * 0,00 € line has bookings behind it is a fact about the OPERATIONS, not about the fact.
   * Every caller in this app passes them.
   */
  operations: OperationInfo[] = [],
): LineSelectability {
  // Layer C — before the 0-magnitude rule below, on purpose: a block whose every class was
  // refused books 0,00 € and still has a story (the reasons). ``disabled`` where the fact
  // books the € but this app refused the block, for the Landkosten reason: falling through
  // would hand an imputed charge to a builder that has nothing to Σ.
  if (key === INVESTITION_KEY) {
    if (row.investition) return 'selectable';
    return row.direct_costs.investition > 0 ? 'disabled' : 'static';
  }
  if (!SOURCE_BUCKET_KEYS.has(key)) return 'static';
  // #2593 — the blanket "0 ⇒ nothing to break down" rule is load-bearing for every other line,
  // so the carve-out is scoped to exactly one shape: a Saatgut line at EXACTLY 0,00 € whose
  // stand block says the zero is the rule's verdict. That line DOES have a derivation worth
  // opening. A negative magnitude is deliberately excluded: the builder would answer 0 against
  // a non-zero lineValue and resurrect the "Σ stimmt nicht" accusation this issue removes.
  const magnitude = lineMagnitude(row, key);
  if (magnitude <= 0) {
    if (magnitude === 0 && key === 'saatgut' && zeroStateStand(row)) return 'selectable';
    // codex r1 P2 — a 0,00 € Verpackung line that counted pieces opens: its rows name the
    // articles no invoice priced, which is the farmer's next move.
    if (magnitude === 0 && key === 'verpackung' && hasCountedMaterial(row)) return 'selectable';
    // HOF-307 — a material line at 0,00 € whose inputs the cascade REFUSED still opens: the
    // rows are what say „Preis fehlt" and name the article, and a farmer who cannot open the
    // line cannot see which article needs a price.
    //
    // HOF-299 dropped the ``source === 'operations'`` half, which had made this carve-out
    // unreachable on exactly the rows it exists for (codex r2, P1). Once the Preisspiegel is
    // the only price, a spray it cannot price contributes 0,00 €, so the cycle carries no
    // money-bearing operation line, so the fact materialises as ``category_fallback`` — and
    // the „Preis fehlt" Posten were persisted but never openable. The derivation is
    // FACT-RESIDENT, so it does not depend on the row's source: the same argument #2695 E
    // makes for the Leistung estimate two branches down. The #2574 objection to opening
    // ``buildOperationsBreakdown`` on a fallback row does not reach here either, because it
    // is about a Σ of money-less operations landing at 0 against a NON-ZERO lineValue; this
    // branch is scoped to a lineValue of exactly 0, so both sides agree by construction.
    if (magnitude === 0 && inputPostenIndex(row, key) !== null) return 'selectable';
    // HOF-467 follow-up — the generalisation of the branch above, for every bucket that has
    // no per-input Posten block of its own (Maschinen, Sonstige, Löhne…).
    //
    // The line already SAYS „Preis fehlt · N Buchungen": ``bucketZeroReason`` found N real
    // bookings serving this bucket and none of them carrying €. Leaving it ``static`` told
    // the farmer that ten records exist and then refused to name them — the same
    // shipped-inert shape codex r2 found on the material line, one bucket over. Opening it
    // lists those bookings with „nicht bewertet" instead of a Betrag and no price link.
    //
    // The #2574 objection to opening ``buildOperationsBreakdown`` where money-less
    // operations would Σ to 0 does not reach here: it is about a Σ of 0 landing against a
    // NON-ZERO lineValue, and this branch is scoped to a lineValue of exactly 0.
    if (magnitude === 0 && bucketZeroReason(row, key, operations)?.state === 'missing_price') {
      return 'selectable';
    }
    // …and the same argument for a 0,00 € Gemeinkosten line: the four D2 pool lines ARE
    // its derivation, and "Hütten — noch nicht erfasst" is precisely what a farmer needs
    // to see. A farmer who cannot open the line cannot find out that the number is zero
    // because nobody has captured the pools, rather than because the pools cost nothing.
    if (magnitude === 0 && key === 'gemeinkosten' && row.d2_pools.length > 0) return 'selectable';
    return 'static';
  }
  // A pooled per-hectare share is FACT-RESIDENT, so its bucket opens on either source —
  // the same argument #2695 E makes for the Leistung estimate and HOF-299 for a refused
  // material Posten. Without this a category_fallback cycle whose Maschinen line is ENTIRELY
  // its share of the farm's diesel renders a non-zero number with no way to open it and see
  // the pot or the §II.4 caveat behind it. Found by codex on this change.
  if (pooledHaRow(row, key)) return 'selectable';
  // The Substrat charge, on the same argument: fact-resident, so Sonstige opens on either
  // source. A category_fallback cycle whose Sonstige is (mostly) its imputed substrate would
  // otherwise show a four-figure number nobody can open to see the running metres behind it.
  // On a fallback row only when the Substrat IS the line (codex r2): the operations builder
  // cannot list category sums, so a mixed line would Σ the Substrat alone against it and mint
  // a false „Σ stimmt nicht". That line keeps the fallback verdict below.
  const substrat = substratRow(row, key);
  if (substrat && (row.source === 'operations' || reconciles(substrat.amount, lineMagnitude(row, key)))) {
    return 'selectable';
  }
  // Gemeinkosten opens whenever there is a D2 pool line to show — which, since the pool
  // set is a contract rather than a payload property, is whenever any pool was captured.
  // The pre-D2 audit array still counts, so a not-yet-re-materialised fact keeps its
  // drill-down instead of losing it at deploy.
  if (key === 'gemeinkosten') {
    return row.d2_pools.some((p) => p.captured) || row.allocations.length > 0 ? 'selectable' : 'disabled';
  }
  if (key === 'saatgut' && row.saatgut_estimate) return 'selectable';
  // #2695 E — the same carve-out the Saatgut estimate gets, and for the same reason: the
  // derivation is FACT-RESIDENT, so it does not depend on the row's source. Without it the
  // Leistung line is ``disabled`` on precisely the rows the estimate exists for — a
  // zero-revenue arable cycle materialises ``category_fallback`` — and the Rechenweg that
  // explains an otherwise-unexplained five-figure € would be unreachable.
  //
  // Gated on the FLAG, not on the block: a flagged row whose block this app rejected must not
  // fall through to the source branch below, where BOTH outcomes lie. On an ``operations`` row
  // it would open the Σ-of-bookings builder, which totals 0,00 € against the estimated line and
  // mints the false "Σ stimmt nicht" the dispatch below exists to prevent; on a fallback row it
  // would render the ``disabled`` tooltip's "stammt aus Kategorien-Summen" (it came from the
  // price book) — and, worse, that branch owns the ``title`` slot, so it would SWALLOW the
  // line's own "kein Erlös gebucht" hover and silently drop the warning. ``disabled`` here is
  // the honest state: there is a story, and this app cannot render it.
  if (key === 'leistung' && row.leistung_estimated) {
    return row.leistung_estimate ? 'selectable' : 'disabled';
  }
  // HOF-102 — a Posten block makes the Leistung line selectable whatever the COST source
  // was. ``source`` records where the cost rows came from (R3a), and since W3 the two
  // questions are independent: a cycle whose harvest is recorded as operations while its
  // costs arrive as cash_flows lands on ``category_fallback`` and still gets a fully
  // priced ``leistung_posten``. Gating the breakdown on the cost source hid the new panel
  // for exactly the arable population it was built to explain. Found by codex.
  if (key === 'leistung' && row.leistung_posten) return 'selectable';
  // HOF-518 — the Landkosten line, on exactly the Saatgut/Leistung argument: the derivation is
  // FACT-RESIDENT, so it opens whatever the cost SOURCE was, and it is the one cascade line a
  // farmer can trace to no document of their own. ``disabled`` on a flagged row whose block
  // this app rejected, for the reason the Leistung branch gives: falling through would hand a
  // land charge to the Σ-of-bookings builder, which totals 0,00 € against it (an imputed rent
  // has no bookings, by construction) and mints a false „Σ stimmt nicht".
  // Scoped to the IMPUTED line: a fact carrying an estimate block, or FLAGGED as estimated
  // with a block this app rejected. A BOOKED Pacht (`exp_lease` → the land bucket) falls
  // through to the ordinary path below and behaves like every other booked bucket — it has
  // real bookings to open, so the Σ-of-bookings objection this branch guards against does
  // not apply to it (codex r3).
  if (key === 'land' && (row.land_estimate || row.land_estimated)) {
    return row.land_estimate ? 'selectable' : 'disabled';
  }
  // #4172 wave 2 — a labour line opens onto its own months, whatever the cost SOURCE was:
  // the evidence is FACT-RESIDENT (``labour_posten``), which is the argument Saatgut, the
  // Leistung estimate and Landkosten each make above. ``disabled`` rather than ``static``
  // where the fact carries the € but no Posten — a pre-Posten fact, or a bucket the engine
  // filled from a derivation rather than from records (Kühlung on a fact that lost its
  // estimate block) — because the line HAS a story and this app cannot render it.
  if (LABOUR_BUCKET_KEYS.has(key)) {
    return labourPosten(row, key).length > 0 || (key === 'kuehlung' && row.kuehlung_estimate) ? 'selectable' : 'disabled';
  }
  // …and ``loehne`` on the same terms when payroll explains it, which a ``category_fallback``
  // fact would otherwise be denied: the Posten are FACT-RESIDENT, so they are there to render
  // whether or not the cycle has a per-operation population (codex round 4).
  if (key === 'loehne' && labourPosten(row, key).length > 0) return 'selectable';
  // HOF-510 — a category-priced line whose applications the Preisspiegel could not price
  // opens onto those applications. The #2574 objection does not reach it: this does NOT open
  // ``buildOperationsBreakdown``, so nothing Σs money-less operations against the line; the
  // dispatch below hands it a breakdown that declares it is not the line's derivation at all.
  //
  // Without this the block naming the unpriced applications is unreachable on exactly the
  // cycles that have them — the refusals are what PUT the row on this path in the first
  // place, by costing 0 and leaving no money-bearing operation line behind.
  if (categoryPricedWithRefusals(row, key)) return 'selectable';
  return row.source === 'operations' ? 'selectable' : 'disabled';
}

/**
 * Build the source breakdown for one cascade-line ``bucket`` of ``row``, or null when
 * the line carries no breakdown (a subtotal/result, or a source-less/disabled line).
 */
export function buildSourceBreakdown(
  row: DeckungsbeitragRow,
  bucket: string,
  operations: OperationInfo[],
): SourceBreakdown | null {
  if (lineSelectability(row, bucket, operations) !== 'selectable') return null;
  if (bucket === 'gemeinkosten') return buildAllocationsBreakdown(row);
  if (bucket === 'saatgut' && row.saatgut_estimate) return buildSaatgutEstimateBreakdown(row);
  // #2695 E — MUST intercept before the operations dispatch below. A cycle valued from the
  // price book books no revenue at all, so the Σ-of-bookings builder would total 0,00 € against
  // a five-figure line and mint a false "Σ stimmt nicht" on the one line whose € is already the
  // hardest to trust.
  if (bucket === 'leistung' && row.leistung_estimate) return buildLeistungEstimateBreakdown(row);
  // HOF-518 — MUST intercept before the operations dispatch, for the reason the two above
  // give: the land charge is IMPUTED, so no booking of the farm's stands behind it and the
  // Σ-of-bookings builder would total 0,00 € against a four-figure line.
  if (bucket === 'land' && row.land_estimate) return buildLandEstimateBreakdown(row);
  // Layer C — fact-resident, and nothing else could build it.
  if (bucket === INVESTITION_KEY) return buildInvestitionBreakdown(row);
  // HOF-510 — MUST intercept before the operations dispatch, for the reason #2695 E gives one
  // line up: the Σ-of-bookings builder would total 0,00 € against a line the invoices priced
  // and mint a false „Σ stimmt nicht". Scoped by ``categoryPricedWithRefusals`` so the
  // operations path and the 0,00 € carve-out both keep the builder they already had.
  if (categoryPricedWithRefusals(row, bucket)) return buildUnpricedPostenBreakdown(row, bucket, operations);
  // #4172 wave 2 — MUST intercept before the operations dispatch, for the reason the three
  // branches above give: a labour line's evidence is the fact's own Posten, and the
  // Σ-of-bookings builder would total 0,00 € against it.
  if (LABOUR_BUCKET_KEYS.has(bucket)) return buildLabourBreakdown(row, bucket);
  if (OPERATIONS_BUCKETS.has(bucket)) {
    const booked = buildOperationsBreakdown(row, bucket, operations);
    // #4172 wave 2 — ``loehne`` is the one bucket BOTH sources feed, so it is the one that
    // cannot simply pick a builder (codex round 4). ``myfarm_mapping`` still writes
    // ``operation.labour`` (person + hours + rate off the price book) and wave 2 routes
    // ``field_care`` / ``overhead`` records into the same bucket; WF has both feeds.
    //
    // Showing one of them understates the panel and mints a false „Σ stimmt nicht" — and on a
    // wiberry cycle, whose Löhne is ENTIRELY payroll, the operations table sums 0,00 € against
    // a line the records fully explain. So the payroll months are APPENDED to the dated
    // bookings and the Σ covers both. A fact with no payroll Posten keeps exactly the panel it
    // had, byte for byte.
    const payroll = labourPosten(row, bucket);
    if (bucket !== 'loehne' || payroll.length === 0) return booked;
    const { items, sumRaw } = labourPostenItems(row, bucket);
    const total = round2(booked.total + sumRaw);
    return { ...booked, items: [...booked.items, ...items], total, reconciles: reconciles(total, booked.lineValue) };
  }
  return null;
}

/** This fact's Posten for one labour LINE, across every bucket it spans — ``[]`` where it carries none. */
function labourPosten(row: DeckungsbeitragRow, bucket: string): LabourPostenItem[] {
  return lineBuckets(bucket).flatMap((member) => row.labour_posten?.[member as keyof typeof row.labour_posten] ?? []);
}

// ── #2476: explain a 0/blank calc-input line ─────────────────────────────────────
/**
 * The cascade calc-input lines that get a #2476 missing-data reason token when their
 * value is 0/blank: the Leistung line + the six direct-cost buckets. Subtotals/results
 * (DkfL/DB1/DB2), Direktzahlung, Etablierung and Gemeinkosten are NOT here — a 0 there
 * is arithmetic (or the line simply isn't rendered), not a missing input.
 */
const REASON_BUCKETS = new Set(['leistung', 'saatgut', 'duenger', 'psm', DUENGER_PSM_LINE, 'loehne', 'maschinen', 'sonstige']);

/**
 * #2476 — the distinguishable states behind a 0/blank calc-input line. #2593 adds a third
 * that is NOT a missing-data state at all: the line is 0,00 € because a rule says so.
 */
export type ZeroReasonState = 'missing_price' | 'no_records' | 'stand_outside_window';

/**
 * #2476 — why a cascade calc-input line reads 0/blank, so the drawer can show a
 * distinguishable token ("Preis fehlt" vs "Keine Erfassung") instead of a bare "—":
 *   - ``missing_price`` — producing records exist but carry no price/amount (show the
 *     record count, or for Leistung the harvested Menge/kg).
 *   - ``no_records`` — nothing recorded for this bucket at all.
 */
export interface ZeroReason {
  state: ZeroReasonState;
  /** Producing records attributable to the bucket (0 for ``no_records``). */
  recordCount: number;
  /** Leistung only — total harvested quantity across picks (0 = picks-but-0-kg). */
  harvestQty?: number;
  /** Leistung only — the harvest quantity's unit (falls back to the row's ``yield_unit``). */
  harvestUnit?: string | null;
  /** #2593 — the stand verdict behind a ``stand_outside_window`` 0,00 €; absent otherwise. */
  stand?: ZeroStateStand;
}

/** #2476 — whether ``key`` is a calc-input line whose 0/blank value needs a reason token. */
export function isMissingInputLine(row: DeckungsbeitragRow, key: string): boolean {
  return REASON_BUCKETS.has(key) && lineMagnitude(row, key) === 0;
}

/**
 * #2574 — whether the served operations may be read as THIS row's records.
 *
 * The SINGLE shared gate for the #2574 carve-out (zero-reason + the drawer's loading wait),
 * so the two surfaces can never drift apart. ``useCropCycleOperations`` fetches per
 * crop_cycle across ALL periods, so the real question is which of the cycle's
 * ``(crop_cycle, period)`` facts those records belong to:
 *
 *   - an ``operations``-source row was COMPUTED from them — attributable by construction.
 *     Kept as the first clause so the gate is strictly ADDITIVE: a stale fact whose cycle
 *     was later re-dated can never lose the #2476 diagnosis it already had.
 *   - a ``category_fallback`` row for the cycle's OWN ``crop_year`` spans exactly the
 *     records served. Since #2536 that is the normal shape of a cycle whose operations are
 *     ALL money-less — recorded, unpriced spray/fertilise — and the case #2476's
 *     "Preis fehlt · N Buchungen" was built for.
 *   - a #1720 thin establishment-only fact for another year is what the gate keeps out: its
 *     0 lines were never computed from the served (other-period) records, so it keeps
 *     reading "Keine Erfassung".
 *
 * Blank-crop_year cycle: null == null → attributable, which is correct — the thin fan-out
 * always stamps a concrete ``str(year)`` period, so a null-period row is never thin. An
 * UNJOINED cycle (null ``cycle_crop_year``, non-null period) therefore degrades to the
 * pre-#2574 "Keine Erfassung", never to a cross-period count.
 *
 * One engine branch does write a thin fact AT the cycle's own crop_year: the #2268 future-WJ
 * belt suppresses the index-0 fact, so the fan-out's "skip the year already emitted" guard
 * does not fire. Such a row would read as attributable here — it is unreachable in the UI only
 * because ``withoutFutureWirtschaftsjahre`` drops future-WJ rows before they are ever rendered.
 * If that filter is ever relaxed, this predicate needs the establishment-only shape as a
 * second discriminator.
 */
export function operationsAttributable(row: DeckungsbeitragRow): boolean {
  return row.source === 'operations' || periodYearKey(row.period) === periodYearKey(row.cycle_crop_year);
}

/**
 * #2574 — the 4-char crop_year key a period value compares on, mirroring the worker's
 * ``_period_key`` (``deckungsbeitrag.py``). BOTH axes route through it for the same reason the
 * engine does: a year-leading value that arrives in a different shape (`` "2020-01-01" ``, a
 * padded `` " 2020" ``) must not silently mismatch its own year. A raw string compare would
 * degrade such a row back to "Keine Erfassung" — the failure is one-directional (never a false
 * count), but the #2559 stub-write history and the myfarm/wiberry dual-writer situation make
 * a reshaped ``crop_year`` a real possibility rather than a hypothetical.
 *
 * Blank/absent → null, so a blank-crop_year cycle still answers null == null.
 */
const periodYearKey = (value: string | null): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed.slice(0, 4) : null;
};

/**
 * #2476 — how many served operations are *records* for a direct-cost ``bucket``,
 * price-independent: a structural labour/machinery entry, a type-classified material op
 * (sow/fertilize/spray), or an explicit ledger override. It NEVER sums money (that stays
 * the materialiser's job) — it only distinguishes "input present, price unknown" from
 * "nothing recorded". A plain pick attributes to no cost bucket.
 */
/**
 * Does this ONE booking serve ``bucket`` — i.e. is it one of the „N Buchungen" the line's
 * „Preis fehlt" badge counts?
 *
 * Extracted from ``bucketRecordCount`` (below, which is now its Σ) because HOF-467's
 * follow-up needs the same question per ROW: a 0,00 € line has to list the bookings behind
 * its badge, and „behind its badge" has to mean exactly what the badge counted, or the panel
 * and the chip disagree about the same ten records.
 */
export function opServesBucket(op: OperationInfo, bucket: string): boolean {
  // Löhne/Maschinen carry a structural line (labour/machinery) independent of the
  // material cost — count it, and don't also count the same op's material bucket here.
  // The fall-through when the structural leg is EMPTY is deliberate and must stay: a
  // ledger override can route an op's material cost onto these buckets, and that op is
  // then served by the material branch below.
  if (bucket === 'loehne' && op.labour.length > 0) return true;
  if (bucket === 'maschinen' && op.machinery.length > 0) return true;
  // Material record: a direct-cost ledger override wins over the op type (parity with
  // costBucket / the materialiser) — a ledger-reclassified op never counts on its type bucket.
  // HOF-299 — a merged line counts the records of every bucket it spans.
  const material = materialCostBucket(op);
  return material != null && lineBuckets(bucket).includes(material);
}

export function bucketRecordCount(operations: OperationInfo[], bucket: string): number {
  let n = 0;
  for (const op of operations) if (opServesBucket(op, bucket)) n += 1;
  return n;
}

/**
 * #2476 — the reason a cascade calc-input ``bucket`` line reads 0/blank, or null when the
 * line is not a calc-input line or already carries a value. DISPLAY/PROVENANCE ONLY: it
 * reads the served records to tell "Preis fehlt" from "Keine Erfassung"; it never
 * re-derives the persisted DB number.
 *
 * #2574 — operation-derived reasons are gated on whether the served records are
 * ATTRIBUTABLE to this row (``operationsAttributable``), NOT on ``source === 'operations'``.
 * The two questions diverged at #2536: a cycle whose operations are ALL money-less now
 * materialises ``category_fallback``, and reading that as "nothing recorded" is exactly
 * backwards — real, unpriced spray/fertilise records is the case "Preis fehlt · N Buchungen"
 * exists to name. The gate still holds the ORIGINAL hole shut: ``useCropCycleOperations`` is
 * fetched per crop_cycle across ALL periods, so a thin establishment-only per-year fact —
 * one whose period is NOT its cycle's own crop_year — still reports a flat "Keine Erfassung"
 * rather than counting another period's records.
 */
export function bucketZeroReason(
  row: DeckungsbeitragRow,
  bucket: string,
  operations: OperationInfo[],
): ZeroReason | null {
  if (!isMissingInputLine(row, bucket)) return null;
  // #2593 — a written-off / not-yet-planted stand's 0,00 € is the rule's VERDICT, not a
  // missing booking. Reported BEFORE the source gate and before any record counting: the
  // verdict is fact-carried, so it holds on a category_fallback row too, and it must win over
  // "Preis fehlt · N Buchungen" on an operations row that happens to serve sow records.
  if (bucket === 'saatgut') {
    const stand = zeroStateStand(row);
    if (stand) return { state: 'stand_outside_window', recordCount: 0, stand };
  }
  if (!operationsAttributable(row)) return { state: 'no_records', recordCount: 0 };
  if (bucket === 'leistung') {
    const picks = operations.filter(isPick);
    if (picks.length === 0) return { state: 'no_records', recordCount: 0 };
    const harvestQty = picks.reduce((sum, op) => sum + (fin(op.qty) ?? 0), 0);
    const harvestUnit = row.yield_unit ?? picks.find((op) => op.unit != null)?.unit ?? null;
    return { state: 'missing_price', recordCount: picks.length, harvestQty, harvestUnit };
  }
  const recordCount = bucketRecordCount(operations, bucket);
  return recordCount > 0
    ? { state: 'missing_price', recordCount }
    : { state: 'no_records', recordCount: 0 };
}
